// @ts-check
// Controlador de composición del editor visual de diagramas (TSK-0027).
//
// Objetivo: mantener un ÚNICO estado central (`programa` + `estadoVisual`,
// ver `editor-model.js`/`editor-visual-state.js`, TSK-0021) y sincronizarlo
// entre `palette-view.js` (TSK-0022), `canvas-view.js` (TSK-0023),
// `block-operations.js` (TSK-0024), `connectors.js` (TSK-0025),
// `editor-persistence.js` (TSK-0026) y `block-content-panel.js` (TSK-0050,
// integrado en TSK-0051), que hasta esta tarea vivían aislados y sin
// componer. Este archivo es el que decide de verdad las ambigüedades que
// TSK-0024, TSK-0025 y TSK-0050 dejaron documentadas y explícitamente
// pendientes para esta tarea de integración (ver "DECISIÓN 1", "DECISIÓN 2" y
// "DECISIÓN 3" abajo).
//
// --------------------------------------------------------------------------
// Por qué "reconstruir" en vez de "mutar in place"
// --------------------------------------------------------------------------
// `canvas-view.js`, `block-operations.js` y `connectors.js` son 3 módulos ya
// "done" (fuera de las rutas permitidas de esta tarea): ninguno expone un
// método para que un controlador externo les IMPONGA un `programa` nuevo
// tras construirse (`canvas-view.js` solo permite AÑADIR vía
// `colocarBloque`; `block-operations.js`/`connectors.js` no exponen ningún
// setter). Cada uno mantiene su propio `programaActual` en su cierre y solo
// se comunica hacia fuera con un callback `onCambioPrograma`.
//
// Sin poder tocar esos 3 módulos, la única forma honesta de que los TRES
// paneles reflejen siempre el MISMO árbol lógico (el criterio real de esta
// tarea: "sincronizarlo") es que, cada vez que cualquiera de ellos notifique
// un cambio, el controlador destruya las 3 instancias y las reconstruya con
// el `programa` ya actualizado. `estadoVisual` NO sufre este problema: es un
// único objeto mutable (`{ posiciones: Map }`, `editor-visual-state.js`) que
// se pasa POR REFERENCIA a las 3 fábricas, así que mover un bloque en
// `canvas-view.js` ya es visible de inmediato para cualquier lectura futura
// de `estadoVisual` sin necesidad de reconstruir nada por eso.
//
// La reconstrucción se difiere a un microtask (`programarReconstruccion`)
// para no destruir un panel EN MITAD de su propio método (los 3 módulos
// llaman a `onCambioPrograma` como penúltimo paso de sus funciones públicas
// y hacen trabajo propio DESPUÉS de notificar — p.ej.
// `block-operations.js#duplicarBloque` fija la posición desplazada de la
// copia después de llamar a `aplicarNuevaSecuencia`, que ya notifica).
// Diferir con `queueMicrotask` garantiza que ese trabajo posterior a la
// notificación (incluida la escritura final en `estadoVisual`) haya
// terminado antes de que el controlador reconstruya el canvas, así el
// bloque duplicado nace ya con su posición desplazada correcta.
//
// --------------------------------------------------------------------------
// DECISIÓN 1 (pendiente explícita de TSK-0024, para architecture-reviewer):
// contrato de selección canvas -> operaciones
// --------------------------------------------------------------------------
// `block-operations.js` consume la selección del canvas por CONTRATO DE DOM
// (`canvasRoot` + `dataset.nodeId` + evento `focusin`), no por import
// directo de `canvas-view.js`. TSK-0024 dejó explícitamente para esta tarea
// decidir si se formaliza con una API real en `canvas-view.js` o si se
// mantiene como decisión definitiva.
//
// Se mantiene el contrato de DOM como decisión DEFINITIVA. Motivo: tanto
// `canvas-view.js` como `block-operations.js` son módulos "done", revisados
// y CERRADOS — no están en las rutas permitidas de TSK-0027 (ver
// `tasks/ready/TSK-0027-editor-integration.md`). Añadir un método de
// selección real a `canvas-view.js` exigiría reabrir un módulo ya aprobado
// fuera del alcance de esta tarea, lo que requeriría su propia tarea con su
// propia revisión (architecture-reviewer + accessibility-reviewer), no una
// decisión unilateral de esta integración. En vez de dejarlo como un detalle
// privado sin verificar, esta tarea lo ELEVA a contrato de integración de
// primer nivel:
//   1. Documentado aquí (antes solo en la cabecera de `block-operations.js`)
//      como parte del contrato público que este controlador depende de que
//      se mantenga estable entre `canvas-view.js` y `block-operations.js`.
//   2. `canvasRoot` que recibe `block-operations.js` es SIEMPRE la raíz del
//      `canvasVista` recién reconstruido (nunca una raíz obsoleta de una
//      instancia ya destruida): al reconstruirse ambos paneles siempre
//      juntos (ver arriba), no hay ninguna ventana en la que
//      `block-operations.js` escuche `focusin` en un `canvasRoot` que ya no
//      esté en el documento.
//   3. Verificado a nivel de composición real (no solo aislado) por
//      `tests/accessibility/diagram-editor/editor-integration.spec.js`, el
//      hueco que accessibility-reviewer señaló explícitamente en TSK-0022 y
//      TSK-0023 ("la equivalencia por teclado hoy solo está demostrada a
//      nivel de test, no en la app compuesta").
// Recomendación explícita para una tarea futura (fuera de alcance aquí): si
// se reabre `canvas-view.js` alguna vez por otro motivo, esa sería la
// ocasión natural de sustituir el contrato de DOM por una API de selección
// real (p.ej. `obtenerBloqueSeleccionado()`/`onSeleccionCambia(cb)`).
//
// --------------------------------------------------------------------------
// DECISIÓN 2 (pendiente explícita de TSK-0025, para architecture-reviewer):
// pool de "bloques sin conectar" de connectors.js
// --------------------------------------------------------------------------
// `connectors.js` mantiene su propia lista en memoria de bloques
// desconectados (`bloquesSinConectar`/`agregarBloqueSinConectar`) porque,
// aislado, no tenía acceso a la composición de página. Esta tarea la conecta
// con el estado real así:
//   - El único `programa` publicado por este controlador ES la fuente de
//     verdad de "qué está conectado": todo lo que cuelga de
//     `programa.cuerpo.sentencias` (o de una rama/cuerpo anidado) está
//     conectado por definición; todo lo demás (el pool) no lo está.
//   - Un bloque solo entra en el pool por la propia acción "Desconectar" de
//     `connectors.js` (no hay ninguna otra vía en los 4 módulos de UI que
//     produzca un nodo huérfano). Al reconstruir `conectoresVista` tras
//     cualquier cambio (ver arriba), el pool de la instancia ANTERIOR se lee
//     con `obtenerBloquesSinConectar()` y se reinyecta como
//     `bloquesSinConectar` de la instancia nueva: sobrevive a la
//     reconstrucción en vez de perderse.
//   - Cuando el usuario pulsa "Conectar aquí" sobre un bloque del pool,
//     `connectors.js` ya lo inserta en el `programa` y lo retira de su
//     propia lista antes de notificar: el controlador reconstruye
//     `canvasVista`/`operacionesVista` con el `programa` actualizado, así
//     que el bloque reconectado aparece de inmediato en el área de trabajo y
//     en el panel de operaciones, sin ningún cableado adicional. No hace
//     falta que el controlador lea/escriba el pool directamente: basta con
//     preservarlo entre reconstrucciones y dejar que `programa` (la única
//     fuente de verdad) fluya al resto de paneles.
// No se necesita ningún mecanismo nuevo aparte de la reconstrucción ya
// descrita: por eso no existe aquí ninguna función "sincronizarPool" — la
// sincronía es una consecuencia de que `programa` sea único y compartido.
//
// --------------------------------------------------------------------------
// DECISIÓN 3 (TSK-0051, integración de `block-content-panel.js`, TSK-0050):
// mismo contrato de DOM y mismo mecanismo de reconstrucción, sin excepciones
// --------------------------------------------------------------------------
// `block-content-panel.js` (TSK-0050) ya se diseñó y documentó a propósito
// para encajar aquí sin ninguna pieza nueva: consume la selección del canvas
// exactamente por el mismo CONTRATO DE DOM que "DECISIÓN 1" (`canvasRoot` +
// `dataset.nodeId` + `focusin`) y notifica sus propios cambios de contenido
// por el mismo `onCambioPrograma` que ya usan canvas/operaciones/conectores.
// Esta tarea se limita a añadirlo como un QUINTO slot (`contenidoSlot`) con
// el idioma ya establecido, sin introducir ningún mecanismo nuevo:
//   1. `crearContenido()` recibía `canvasRoot: canvasVista.elemento` —
//      SIEMPRE la raíz del canvas recién reconstruido, nunca una obsoleta—
//      igual que `crearOperaciones()` (ver "DECISIÓN 1", punto 2): los 4
//      paneles dependientes (canvas, operaciones, conectores, contenido) se
//      destruían y recreaban siempre juntos en `reconstruirDependientes()`.
//      ACTUALIZADO por TSK-0088 ("DECISIÓN 10", más abajo en este archivo):
//      ya NO es así para el origen `'contenido'` -- ver esa sección para el
//      motivo (evitar destruir el `<input>` en el que escribe el usuario) y
//      el nuevo `canvasRoot: canvasSlot`.
//   2. Una edición de un campo del panel llama a `onCambioPrograma` con
//      origen `'contenido'`; `manejarCambioPrograma` la trata exactamente
//      igual que cualquier otro origen: reemplaza el `programa` central y
//      programa la reconstrucción vía `queueMicrotask`. El resto de paneles
//      (incluido el propio canvas, que puede renombrar la etiqueta de un
//      bloque tras editar su contenido) se reconstruyen a partir de ese
//      único `programa`, sin ningún cableado adicional específico de este
//      panel.
//   3. Riesgo NUEVO que ni "DECISIÓN 1" ni "DECISIÓN 2" tenían: operaciones y
//      conectores solo notifican tras acciones DISCRETAS (pulsar un botón),
//      pero `block-content-panel.js` notifica en cada PULSACIÓN de un
//      `<input>` de texto (ver su cabecera, "Estabilidad del foco mientras
//      se escribe") y `expression-editor.js` (TSK-0049, "done", fuera de
//      rutas permitidas, montado DENTRO de este panel para los campos de
//      tipo expresión) notifica también al cambiar su `<input
//      type="number">` de literal o su `<input type="radio">` de modo. Si
//      esta reconstrucción se limitara a "destruir y recrear las 4
//      instancias" como el resto de orígenes, cada una de esas
//      interacciones destruiría el propio control en el que está el
//      usuario, robándole el foco Y la selección de bloque (la instancia
//      nueva de `block-content-panel.js` nace con `idSeleccionadoEnCanvas =
//      null`, sin memoria de la anterior) en MITAD de la edición — una
//      regresión de accesibilidad real (WCAG 2.2 §2.4.3 "Focus Order"/foco
//      perdido), no solo una molestia de rendimiento. Por eso, únicamente
//      cuando `origenUltimoCambio === 'contenido'`,
//      `reconstruirDependientes()` captura ANTES de destruir
//      (`capturarFocoContenido`) qué bloque estaba seleccionado y, si el
//      foco vivía en CUALQUIER `<input>` de este panel (propio o de
//      `expression-editor.js`, sin importar su `type`), en qué posición de
//      esa lista. Tras recrear las 4 instancias, `restaurarFocoContenido`
//      SIEMPRE reselecciona ese mismo bloque en la instancia NUEVA y
//      devuelve el foco al `<input>` equivalente — la selección de bloque y
//      el control enfocado se restauran para cualquier `type`.
//
// --------------------------------------------------------------------------
// DECISIÓN 4 (TSK-0061, DEC-027): el panel de operaciones deja de montarse
// --------------------------------------------------------------------------
// `block-operations.js` (TSK-0024, "DECISIÓN 1" arriba) queda DEPRECADO por
// TSK-0061: su papelera + Supr/Backspace + "duplicar" mínimo (todos en
// `canvas-view.js`, TSK-0060/0061) sustituyen a este panel, incluida su
// confirmación de dos pasos para "Eliminar" (sustituida ahí por un deshacer
// inmediato, ver el comentario de cabecera de `canvas-view.js`, sección
// "TSK-0061"). Este controlador simplemente DEJA DE IMPORTAR/MONTAR
// `block-operations.js` (quinto slot `operacionesSlot` retirado del layout;
// `crearOperaciones()`, `operacionesVista` y el origen de cambio
// `'operaciones'` eliminados de este archivo) — el ARCHIVO
// `block-operations.js` en sí NO se toca (fuera de las rutas permitidas de
// TSK-0061; ver su `excepcion_reapertura`): queda huérfano en el árbol,
// registrado aquí para una futura tarea de limpieza que lo borre si nada
// más lo usa.
//
// Contradicción registrada explícitamente (CLAUDE.md, "no decidas
// silenciosamente"): un mensaje de contexto recibido a mitad de TSK-0061
// afirmaba que este archivo estaba en `rutas_prohibidas` de esa tarea y que
// retirar este montaje correspondía a una tarea posterior (TSK-0062). El
// frontmatter real de `tasks/ready/TSK-0061-trash-can-delete.md` dice lo
// contrario: `editor-controller.js` SÍ está en sus `rutas_permitidas`, y su
// propio criterio de aceptación exige textualmente "el panel de operaciones
// deja de montarse en editor-controller.js". Se ha priorizado el frontmatter
// de la ficha (fuente de verdad primaria, coherente además con la
// `excepcion_reapertura` de esa misma ficha) sobre el mensaje de contexto
// parafraseado. Si TSK-0062 existe y se solapa con este cambio, esa tarea
// futura debería encontrar el trabajo ya hecho aquí y limitarse a verificar/
// completar lo que falte (p.ej. borrar `block-operations.js` si queda sin
// ningún otro uso).
//
// --------------------------------------------------------------------------
// DECISIÓN 9 (TSK-0085, CAP-IDENTITY-AUTH-FLOW): cabecera de sesión
// opcional, sin importar `state/**` ni `services/**`
// --------------------------------------------------------------------------
// `frontend/src/pages/diagram-editor-page.js` (TSK-0085) protege esta
// página con `state/session-store.js` (comprueba sesión, redirige a
// `login.html` si no la hay). Este controlador, sin embargo, sigue siendo
// una FEATURE reutilizable que "funciona SIN cuenta" (ver "Reglas no
// negociables" en el docstring de `crearControladorEditor` más abajo, sin
// cambios): no importa `state/**` ni `services/**` (docs/03-frontend.md:
// "components no llaman fetch; services no tocan el DOM" -- este archivo es
// el "component"/feature de composición, la orquestación de sesión es
// responsabilidad exclusiva de la página).
//
// Por eso la cabecera de usuario (`crearTopbar`, más abajo) es puramente
// OPCIONAL y basada en los datos que la propia página ya resolvió: si
// `opciones.usuario` es `null`/no se pasa (p.ej. cualquier otro futuro
// consumidor standalone de esta feature, o los tests existentes de este
// directorio que construyen el controlador sin ese argumento), no se monta
// ninguna cabecera y el resto del editor se comporta exactamente igual que
// antes de esta tarea -- ningún test previo de `crearControladorEditor()`
// se ve afectado. El nombre/rol mostrados son SIEMPRE los que la página
// entrega (originados en `session-store.js#obtenerUsuario()`, una copia,
// nunca la referencia interna del store, ver ese archivo): este controlador
// no valida ni reinterpreta esos campos, solo los muestra como texto.
//
// El botón "Cerrar sesión" tampoco llama a `cerrarSesion()` directamente
// (evitaría el mismo import de `state/**` que se quiere evitar aquí):
// invoca `opciones.onCerrarSesion`, un callback opaco que la página
// construye ya con la orquestación completa (llamar a `cerrarSesion()` +
// decidir qué pasa si falla + redirigir a `login.html`, ver el docstring de
// `manejarCerrarSesion` en `diagram-editor-page.js` para la decisión
// documentada sobre el fallo de red). Este controlador solo se encarga de
// deshabilitar el botón mientras esa promesa está pendiente (evita doble
// clic) y de no dejar la promesa devuelta sin manejar si `onCerrarSesion`
// llegase a rechazar (red de seguridad puramente defensiva: por contrato,
// `manejarCerrarSesion` en la página SIEMPRE resuelve, nunca rechaza).
//
//      Excepción, más estrecha, para la POSICIÓN DE CURSOR: la
//      especificación WHATWG solo permite leer/escribir
//      `selectionStart`/`setSelectionRange` en los `<input>` de tipo
//      `text`/`search`/`url`/`tel`/`password` (más el `type` por defecto,
//      que HTML trata como `text`); cualquier otro tipo (`number`, `radio`,
//      `checkbox`, `range`, `color`, `date`...) LANZA `InvalidStateError`
//      en un navegador real al leerlo/escribirlo. `capturarFocoContenido`/
//      `restaurarFocoContenido` comprueban explícitamente `type`
//      (`TIPOS_INPUT_CON_SELECCION_DE_TEXTO`) antes de tocar esa propiedad:
//      si el `type` no la soporta, se restaura igualmente la selección de
//      bloque y el foco al control correcto, simplemente sin intentar fijar
//      una posición de cursor concreta (no aplica a un `<input
//      type="number">`/`type="radio">`).
//
//      Hallazgo P1 de accessibility-reviewer (primera revisión de
//      TSK-0051): la versión original de esta función NO distinguía por
//      `type` y leía `selectionStart` de CUALQUIER `<input>` enfocado,
//      incluidos los `type="number"`/`type="radio"` de `expression-
//      editor.js` (literales ENTERO/DECIMAL y selector de modo). JSDOM (el
//      entorno de test) es más permisivo que un navegador real y devuelve
//      `null` en silencio ahí donde un navegador real lanza
//      `InvalidStateError`, lo que ocultó el fallo hasta la revisión
//      manual: editar un literal numérico o cambiar el modo de una
//      expresión lanzaba esa excepción DENTRO de `capturarFocoContenido`,
//      ANTES de que `reconstruirDependientes` llegase a destruir/recrear
//      nada — el `queueMicrotask` abortaba sin reconstruir ningún panel,
//      dejando el `programa` central desincronizado del DOM: la misma
//      regresión de foco que esta función pretendía evitar, causada por
//      ella misma. Verificado por
//      `tests/accessibility/diagram-editor/block-content-integration.spec.js`
//      (TSK-0051), incluida una escritura de varios caracteres seguidos sin
//      perder el foco del campo y una edición de un literal numérico/cambio
//      de modo de expresión que ya no lanza ninguna excepción y conserva la
//      selección del bloque.
//
// --------------------------------------------------------------------------
// DECISIÓN 6 (TSK-0064, petición directa del usuario, absorbe TSK-0065):
// el panel de contenido se ancla junto al bloque seleccionado + bloques
// compactos
// --------------------------------------------------------------------------
// Feedback directo del usuario en vivo (ver frontmatter de la ficha): el
// panel "Contenido del bloque seleccionado" (`block-content-panel.js`,
// "DECISIÓN 3" arriba) vivía en su propia fila de grid a todo lo ancho,
// visualmente lejos del bloque que se está editando. `block-content-panel.js`
// y `expression-editor.js` están en `rutas_prohibidas` de esta tarea (se
// consumen por su API pública ya existente): esta tarea NUNCA les añade
// lógica de posicionamiento propia, solo cambia DÓNDE vive/se posiciona su
// contenedor (`contenidoSlot`, ya existente, creado por este mismo archivo).
//
// Mecanismo (ver `crearCanvas`/`posicionarPanelContenido` más abajo):
//   1. `contenidoSlot` deja de ser un hijo de `layout` en su propia fila de
//      grid ("content"): pasa a ser un hijo DOM de `canvasSlot`
//      (`.diagram-editor-slot-canvas`, ahora `position: relative` en
//      `diagram-editor.css`), reinsertado en cada `crearCanvas()` (que
//      reconstruye `canvasSlot` con `replaceChildren`). El propio nodo
//      `contenidoSlot` NUNCA se recrea (sigue siendo el mismo `<div>` estable
//      de siempre, solo su CONTENIDO -- `contenidoVista.elemento` -- se
//      reemplaza en `crearContenido()`): moverlo de contenedor padre no
//      afecta al foco que pueda vivir dentro de él en ese instante (mover un
//      nodo con `appendChild`/`replaceChildren` NO le hace perder el foco a
//      sus descendientes, a diferencia de sustituirlo por uno nuevo).
//   2. `posicionarPanelContenido()` calcula, con `getBoundingClientRect` (el
//      mismo mecanismo, ya aprobado, que usa `canvas-view.js` para la capa de
//      flechas de TSK-0056/DEC-027), la posición del bloque con
//      `dataset.nodeId` igual al id seleccionado
//      (`contenidoVista.obtenerIdSeleccionadoEnCanvas()`, API pública ya
//      existente) y fija dos custom properties CSS
//      (`--content-anchor-top`/`--content-anchor-left`) que `diagram-editor.css`
//      consume para posicionar `contenidoSlot` con `position: absolute`
//      relativo a `canvasSlot`. Se recalcula: al reconstruir (tras cualquier
//      cambio estructural), en cada `focusin` del propio canvas (cambio de
//      selección SIN reconstrucción, p.ej. tabular de un bloque a otro) y en
//      el `scroll` interno de `.canvas-surface` (mismo motivo ya documentado
//      en `canvas-view.js` para las flechas: desplazar esa lista cambia la
//      posición relativa de todo lo que se ancla a un bloque suyo).
//   3. Si hay hueco horizontal suficiente a la derecha del bloque (el que
//      dejan libres los bloques ahora compactos, ver punto siguiente), el
//      panel se ancla A SU LADO; si no, se ancla JUSTO DEBAJO -- ambas
//      opciones son las que describe textualmente el criterio de aceptación
//      ("justo debajo/al lado de su forma"), decidida en tiempo de ejecución
//      según el espacio real disponible, nunca fija de antemano.
//
// Excepción NARROW y documentada al principio general de DEC-027/ADR-0005
// ("un bloque nunca se posiciona con x/y, solo por su índice en la
// Secuencia"): esta decisión NO reintroduce posicionamiento libre de
// bloques -- ningún `.canvas-block` cambia de posición ni de mecanismo de
// posicionamiento por esto, `canvas-view.js` no se toca. Lo que se ancla con
// coordenadas dinámicas es un panel SATÉLITE ajeno a la lista de sentencias
// (igual que ya lo es, sin objeción previa de architecture-reviewer, la capa
// de flechas SVG). Documentado aquí explícitamente para que quede claro que
// es la MISMA categoría de excepción ya concedida, no una nueva.
//
// Tamaño compacto de bloque (petición del usuario, absorbe el objetivo de
// TSK-0065, fusionada aquí porque ambas tocan `canvas-view.js`/CSS del
// canvas -- ver frontmatter): `canvas.css`/`diagram-editor.css` cambian el
// ancho de `.canvas-block` de "100% de la fila" a "ajustado al contenido, con
// un máximo razonable" (ver comentarios junto a esas reglas). Este archivo no
// necesita ningún cambio propio para ESO (es puramente CSS), pero es la
// PRECONDICIÓN de la que depende el punto 3 de arriba: sin bloques compactos
// no habría ningún hueco horizontal real que medir para decidir "al lado" en
// vez de "debajo".
//
// --------------------------------------------------------------------------
// DECISIÓN 5 (TSK-0062, DEC-027): el panel de conectores deja de montarse
// --------------------------------------------------------------------------
// `connectors.js` (TSK-0025, "DECISIÓN 2" arriba) queda DEPRECADO por
// TSK-0062, mismo patrón exacto que "DECISIÓN 4" (TSK-0061/block-
// operations.js): con el modelo de lista vertical secuencial (TSK-0060,
// DEC-027) la posición visual de un bloque de nivel superior ES su índice en
// `programa.cuerpo.sentencias`, así que "conectar A con B" deja de ser una
// acción independiente (elegir un contenedor destino en un `<select>`): es
// simplemente el orden en que los bloques aparecen en la lista. "Eliminar
// una conexión" deja de existir como concepto propio — no puede quedar un
// hueco "desconectado" entre dos bloques de una lista secuencial: solo
// existen "reordenar" (arrastre vertical/teclado, TSK-0060) y "eliminar
// bloque" (papelera/Supr, TSK-0061). Ningún caso de uso previamente cubierto
// por el panel de conectores queda sin alternativa salvo ese, que se
// documenta como reinterpretado (ver DEC-027 en
// docs/generated/decisions.md y docs/03-frontend.md), no como una función
// pendiente de reimplementar.
//
// Este controlador simplemente DEJA DE IMPORTAR/MONTAR `connectors.js`
// (slot `conectoresSlot` retirado del layout; `crearConectores()`,
// `conectoresVista` y el origen de cambio `'conectores'` eliminados de este
// archivo) — el ARCHIVO `connectors.js` en sí NO se toca (fuera de las
// rutas permitidas de TSK-0062): queda huérfano en el árbol, exactamente
// igual que `block-operations.js` tras TSK-0061 (ver "DECISIÓN 4"),
// registrado aquí para una futura tarea de limpieza que borre ambos si
// nada más los usa (ninguna otra vista de esta feature importa
// `connectors.js` ni `block-operations.js`: `canvas-view.js` ya construye
// el reordenado/inserción/eliminación directamente sobre `editor-model.js`
// desde TSK-0060/0061).
//
// El pool de "bloques sin conectar" que `connectors.js` mantenía
// (ver "DECISIÓN 2") deja de tener sentido de producto: en el modelo de
// lista vertical no existe ningún bloque "desconectado pendiente de
// reinsertar" (eliminar SIEMPRE quita de la Secuencia real, con Deshacer
// inmediato, TSK-0061; no hay un estado intermedio "fuera de la lista pero
// no eliminado"). Por eso este controlador tampoco preserva ese pool entre
// reconstrucciones: simplemente no existe ya ninguna instancia de
// `connectors.js` de la que leerlo.
//
// --------------------------------------------------------------------------
// DECISIÓN 7 (TSK-0078, DEC-029, petición directa del usuario): diagramas de
// función mediante un "Programa envoltorio", sin tocar canvas-view.js
// --------------------------------------------------------------------------
// El dominio y el motor de ejecución (backend/src/app/domain/ir.py,
// diagram_engine.py) ya soportan `Funcion`/`Llamada`/`Retorno` y
// `Programa.funciones: list[Funcion]` por completo; el hueco era solo
// visual: no existía ningún mecanismo para crear una función ni para
// navegar a su propio diagrama. `canvas-view.js` y `block-content-panel.js`
// son módulos "done" (rutas_prohibidas de esta tarea) que exigen recibir un
// `Programa` con `cuerpo.nodo === 'Secuencia'` (`requerirPrograma` en
// ambos). En vez de reabrirlos, este controlador construye, SOLO cuando el
// diagrama activo es una función, un Programa SINTÉTICO
// (`programaParaVista()`, más abajo): `{ ...programa, cuerpo: funcion.cuerpo
// }`. Desde la perspectiva de `canvas-view.js`/`block-content-panel.js`
// sigue siendo "un Programa" normal (mismo `nodo: 'Programa'`, mismo
// `cuerpo.nodo === 'Secuencia'`), así que TODA la maquinaria ya construida
// (ramas, flechas, panel de contenido, papelera, teclado, TSK-0055..0073) se
// reutiliza sin ningún cambio en esos dos archivos.
//
// Reconciliación (`manejarCambioPrograma`): cuando `diagramaActivo` es una
// función, el `cuerpo` que devuelve el Programa envoltorio tras un cambio
// NUNCA se escribe en `programa.cuerpo` (el principal) -- se reconcilia con
// `actualizarCuerpoFuncion` (editor-model.js) en
// `programa.funciones[i].cuerpo`, de forma inmutable. Construir/editar
// dentro del diagrama de una función nunca puede afectar al programa
// principal ni a otra función: cada `Funcion.cuerpo` es un subárbol propio e
// independiente (distintos `id`, ninguna estructura compartida entre
// funciones ni con el `cuerpo` principal).
//
// Ejecución (criterio de aceptación de TSK-0078): `crearEjecucion()` recibe
// SIEMPRE el `programa` CENTRAL completo (nunca `programaParaVista()`) --
// "Ejecutar" ejecuta siempre el programa principal completo (que ya incluye
// todas las funciones definidas vía `Programa.funciones`, el motor ya sabe
// resolver `Llamada` contra ellas), nunca el cuerpo aislado de la función
// que se esté viendo en ese momento. Para evitar la ambigüedad obvia de
// pulsar "Ejecutar" mientras se ve el diagrama de una función, se muestra un
// aviso textual explícito junto al panel de ejecución en ese caso (ver
// `crearEjecucion()`).
//
// Autoguardado (`editor-persistence.js`, TSK-0026, sin cambios): tanto
// `flushGuardadoInmediato` como `crearAutoguardadoLocal` cierran sobre la
// variable `programa` (el `let` central de este módulo), nunca sobre
// `programaParaVista()`: el envoltorio es una vista de solo lectura para dos
// paneles concretos, no un estado alternativo -- el autoguardado (y
// `obtenerPrograma()`, expuesto por este controlador) siempre ve/persiste el
// `programa` real con todas sus `funciones`, incluida la que se esté
// editando en ese momento (ya reconciliada por `manejarCambioPrograma` antes
// de que se dispare el autoguardado con debounce).
//
// Anclas Inicio/Fin en el diagrama de una función (decisión de diseño
// abierta en el frontmatter de la ficha): SÍ tienen sentido, sin necesidad
// de ningún cambio de código propio. `canvas-view.js` ya dibuja sus anclas
// fijas a partir de CUALQUIER `programa.cuerpo` que reciba (TSK-0055,
// DEC-026) -- no distingue "el programa principal" de "un cuerpo de
// función": para ese módulo el envoltorio de una función es indistinguible
// de un programa cualquiera con una Secuencia vacía. El resultado es
// exactamente la recomendación del coordinador: cada diagrama de función se
// comporta como un flowchart autocontenido con su propio Inicio/Fin, sin
// necesitar ninguna excepción ni bandera especial.
//
// Nombres de función (criterio de aceptación: "nombre válido de
// identificador, mismo estándar ya usado... en block-content-panel.js"):
// ese módulo no exporta su regex/validador (`PATRON_IDENTIFICADOR`/
// `validarIdentificador` son privados, sin exportar -- ver su cabecera,
// "Formato mínimo de identificador"), así que este controlador la duplica
// como regex trivial, exactamente el mismo precedente ya documentado ahí
// ("se duplica aquí como regex trivial, no como lógica de negocio
// reutilizable"; ese propio comentario ya admite que `expression-editor.js`
// duplicó la misma constante antes). Se añade, además, un rechazo de nombre
// YA USADO por otra función (ni el frontmatter ni el criterio de aceptación
// lo piden explícitamente, pero dos funciones con el mismo `nombre` harían
// ambigua cualquier `Llamada.nombre_funcion` que las invoque -- el motor
// resuelve por nombre, `diagram_engine.py` -- así que se documenta aquí como
// una validación mínima directamente derivada de "nombre válido", no como
// una ampliación de alcance de parámetros/tipo de retorno, que sí quedan
// diferidos a propósito (DEC-029) y no se tocan.
//
// --------------------------------------------------------------------------
// DECISIÓN 8 (TSK-0080, cierra el hueco funcional dejado por TSK-0078):
// eliminar una función desde su propia pestaña
// --------------------------------------------------------------------------
// TSK-0078 dejó crear funciones pero ningún mecanismo para deshacer el
// error de crear una con el nombre equivocado. Se añade un control de
// borrado por PESTAÑA de función (nunca en "Principal", que no es una
// función: no tiene `id` en `programa.funciones` sobre el que operar
// `eliminarFuncion`) dentro de `crearBotonDiagrama()`, con el MISMO patrón
// de confirmación en dos pasos ya aprobado en `block-operations.js`
// (TSK-0024, "Confirmar eliminar X"/"Cancelar", ver su `crearFila()`) en vez
// de `window.confirm()` (bloquea el hilo principal, su copy/estilo no son
// controlables desde este módulo y ya hay un precedente accesible propio en
// este mismo dominio, no hace falta introducir uno nuevo).
//
// Estado (`idPendienteConfirmarEliminarFuncion`) es del mismo tipo/vida que
// `idPendienteConfirmarEliminar` en `block-operations.js`: un único id "en
// confirmación" a la vez, se limpia al confirmar, cancelar o cambiar de
// diagrama/crear otra función (cualquier `renderizarPestanasDiagramas()`
// completo ya lo tiene en cuenta porque lee la variable directamente, sin
// caché).
//
// Si la función eliminada era `diagramaActivo`, se vuelve a `'principal'`
// ANTES de reconstruir canvas/contenido/ejecución (mismo mecanismo que
// `cambiarDiagramaActivo`): así nunca hay un instante en el que esos tres
// paneles se reconstruyan pidiendo `programaParaVista()` de una función que
// ya no existe en `programa.funciones` (`funcionActiva()` devolvería
// `null`, y `programaParaVista()` ya maneja ese `null` cayendo al programa
// central -- pero fijar `diagramaActivo = 'principal'` explícitamente evita
// además que la pestaña "fantasma" siga marcada `aria-current` tras el
// borrado). Eliminar una función NO es un cambio de "contenido"/"canvas"
// (no pasa por `manejarCambioPrograma`): igual que crear una función nueva
// (`manejarSubmitNuevaFuncion`, arriba), actualiza `programa` directamente y
// dispara `autoguardado.notificarCambio()` + `renderizarPestanasDiagramas()`
// + `reconstruirDependientes()` (esta última solo si la pestaña activa
// cambió; si se borra una función NO activa, el diagrama visible no debe
// tocarse en absoluto, criterio de aceptación de la ficha).
//
// --------------------------------------------------------------------------
// DECISIÓN 10 (TSK-0088, petición directa del usuario): popup real de
// apertura/cierre por clic + fin de la reconstrucción completa en cada tecla
// --------------------------------------------------------------------------
// Causa raíz confirmada por investigación previa a esta tarea (no un
// problema de "click-outside", como se sospechaba al principio): CUALQUIER
// edición de un campo del panel de contenido (`block-content-panel.js`, un
// `<input>` de texto o los controles de `expression-editor.js`) notifica
// `onCambioPrograma(programa, 'contenido')` en CADA pulsación de tecla (ver
// "DECISIÓN 3" más arriba), y `manejarCambioPrograma` trataba ese origen
// exactamente igual que cualquier otro: `programarReconstruccion()`
// (`queueMicrotask`) que `reconstruirDependientes()` ejecuta destruyendo y
// recreando canvas, CONTENIDO y ejecución COMPLETOS. Como cada pulsación de
// teclado es su propia macrotarea del navegador, el resultado observable era
// "todo el editor se destruye y reconstruye en cada tecla" -- la causa raíz
// real de la sensación de "caos"/"desaparece" reportada por el usuario, ya
// mitigada parcialmente (nunca resuelta del todo) por
// `capturarFocoContenido`/`restaurarFocoContenido`.
//
// CONTRADICCIÓN DESCUBIERTA Y REGISTRADA EXPLÍCITAMENTE (CLAUDE.md, "no
// decidas silenciosamente"): la ficha de esta tarea sugería, como primera
// alternativa, un DEBOUNCE completo de `reconstruirDependientes()` (difiere
// TODO -- canvas, contenido, ejecución -- con un `setTimeout`, cancelado y
// reiniciado en cada tecla). Se implementó primero exactamente así y se
// verificó con una reproducción real (no una suposición): al colocar un
// bloque nuevo desde la paleta (origen `'canvas'`) MIENTRAS una edición de
// contenido seguía "en pausa" dentro de la ventana de debounce, el
// `Entrada.variable` recién editado se REVERTÍA silenciosamente a su valor
// de relleno original. Causa raíz de esa regresión: `canvas-view.js` (módulo
// "done", fuera de las rutas permitidas de esta tarea) mantiene su PROPIA
// copia interna de `programaActual` en su cierre, que solo se resincroniza
// con el `programa` central cuando el CONTROLADOR lo destruye y reconstruye
// con un `programa` fresco -- es su único canal de entrada de cambios
// externos, no tiene ningún método para "refrescar" ese dato sin
// reconstruirse entero. Si se difiere la reconstrucción de `canvasVista`
// mientras el usuario escribe, cualquier acción estructural que el usuario
// realice DIRECTAMENTE sobre el canvas en ese ínterin (colocar un bloque
// nuevo, arrastrar, eliminar, reordenar con teclado) opera sobre esa copia
// interna DESACTUALIZADA -- y el `programa` que esa acción devuelve por
// `onCambioPrograma('canvas')` PIERDE la edición de contenido pendiente, sin
// ningún aviso. No hay ningún punto de intercepción posible antes de que
// `canvas-view.js` calcule ese resultado (sus propios listeners internos de
// arrastre/teclado/paleta no pasan por ningún hook de este controlador antes
// de actuar): la única forma de evitarlo de raíz es que `canvasVista` NUNCA
// quede desactualizada respecto al `programa` central mientras el usuario
// pueda interactuar con ella -- es decir, NO diferir su reconstrucción.
//
// Solución implementada (ajuste de alcance respecto a la ficha original,
// documentado aquí en vez de aplicado en silencio): se reconstruye
// `canvasVista`/`ejecucionVista` de inmediato en CUALQUIER cambio (mismo
// mecanismo `programarReconstruccion()`/`queueMicrotask` que ya existía,
// SIN debounce -- necesario para la corrección de datos, ver arriba), pero
// `contenidoVista` (el panel donde vive literalmente el foco/cursor del
// usuario mientras escribe) NUNCA se destruye ni se recrea cuando el origen
// del cambio es `'contenido'` -- ver `reconstruirDependientes()`, más abajo.
// Esto resuelve el síntoma concreto y más dañino reportado ("el foco del
// campo que se está editando NUNCA debe perderse mientras el usuario
// escribe"): al no destruirse jamás, no hay nada que "perder" ni que
// restaurar -- el `<input>` en el que escribe el usuario es literalmente el
// MISMO nodo DOM antes y después de cada pulsación, sin flicker, sin
// interrupción de composición IME, sin coste de reconstrucción de ese
// subárbol. `programa` (el estado central que lee `editor-persistence.js`
// para autoguardar y `execution-panel.js` para ejecutar) se actualiza de
// forma SÍNCRONA en cada pulsación, exactamente igual que antes.
//
// Contrapartida asumida y documentada (no oculta): `canvasVista`/
// `ejecucionVista` SÍ se siguen reconstruyendo una vez por pulsación,
// exactamente igual que antes de esta tarea -- el criterio de aceptación
// literal ("ya no destruye/reconstruye canvas-view... en cada pulsación de
// tecla") no queda satisfecho al 100% para esas dos vistas por el motivo de
// corrección de datos ya explicado. Se prioriza NUNCA perder silenciosamente
// una edición del usuario por encima de eliminar por completo un
// refresco de fondo en una parte de la pantalla que NO tiene el foco
// mientras se escribe (el subárbol de `contenidoVista` vive en un
// contenedor DOM aparte, `contenidoSlot`, nunca dentro de `canvasVista.
// elemento`) -- el propio `capturarFocoContenido`/`restaurarFocoContenido`
// (DECISIÓN 3) ya demuestra que el foco sobrevive incluso a una
// reconstrucción completa de canvas si hiciera falta, pero para el origen
// `'contenido'` ya no hace falta invocarlo en absoluto (contenidoVista nunca
// se toca). Recomendación explícita para una tarea futura (fuera de alcance
// aquí, exige reabrir `canvas-view.js`): exponer un método real de
// "refrescar programa sin reconstruir" en ese módulo permitiría diferir
// también su reconstrucción con seguridad.
//
// Consecuencia de mantener viva la MISMA instancia de `contenidoVista` a
// través de reconstrucciones de `canvasVista` (que SÍ cambia de nodo DOM en
// cada una): su contrato de `canvasRoot` (DECISIÓN 1/3, `dataset.nodeId` +
// eventos delegados) ya NO puede apuntar a `canvasVista.elemento` (se
// destruye y sustituye en cada reconstrucción de canvas, dejando esos
// listeners escuchando un árbol DOM huérfano fuera del documento). Se le
// pasa en su lugar `canvasSlot` -- el `<div>` contenedor ESTABLE que este
// controlador nunca destruye (solo reemplaza sus hijos vía
// `canvasSlot.replaceChildren(canvasVista.elemento, contenidoSlot)` en cada
// `crearCanvas()`, ver más abajo): los eventos delegados de
// `block-content-panel.js` siguen recibiendo los `click`/`mousedown`/
// `focusin` de CUALQUIER botón de bloque presente en cada momento,
// sobreviva o no la instancia concreta de `canvasVista` que lo contiene.
// Como `contenidoSlot` (que aloja el propio `<section class="block-content-
// panel">` de `contenidoVista`) también es descendiente de `canvasSlot`,
// `block-content-panel.js` ignora explícitamente cualquier evento cuyo
// objetivo esté DENTRO de su propia `<section>` (ver su cabecera, "TSK-0088"
// -- guarda añadida en `idDeNodoDesdeEvento`): sin ese guardián, escribir en
// un campo propio (que no tiene `data-node-id`) se interpretaría como "clic
// fuera de cualquier bloque" y cerraría el propio panel en el que se está
// escribiendo.
//
// Popup real de apertura/cierre por clic (segunda mitad de esta tarea,
// implementada en `block-content-panel.js`, "TSK-0088" en su cabecera):
// clic en un bloque sin panel abierto lo abre; clic en el MISMO bloque con
// el panel ya abierto lo cierra; clic en OTRO bloque cierra el anterior y
// abre el nuevo. Esa lógica de toggle vive ENTERA en `block-content-
// panel.js` (consume sus propios eventos `mousedown`/`click` sobre
// `canvasRoot`, igual que ya hacía con `focusin`): este controlador no
// duplica ninguna decisión de "abrir/cerrar", solo reacciona a que la
// selección cambió. La reacción concreta que le toca a este controlador es
// puramente VISUAL: `contenidoSlot` (el contenedor que este archivo posee y
// posiciona, ver "DECISIÓN 6") pasa a OCULTARSE por completo
// (`contenidoSlot.hidden`) cuando no hay ningún bloque seleccionado --
// antes de esta tarea el panel seguía siempre visible en su posición de
// flujo normal mostrando el mensaje "Selecciona un bloque...", lo que no
// transmitía la sensación de "popup que se cierra" que pide el criterio de
// aceptación. `block-content-panel.js` en sí NO oculta su propia `<section>`
// (evaluado y descartado a propósito: sus propios tests unitarios/de
// accesibilidad, fuera de las rutas permitidas de esta tarea salvo por los
// archivos explícitamente listados, montan el panel AISLADO y consultan su
// contenido/rol sin seleccionar nada -- ocultarlo ahí habría exigido
// reescribir esa suite entera sin necesidad real, cuando la composición
// real ya tiene un contenedor propio, `contenidoSlot`, perfecto para esta
// responsabilidad puramente de composición).
//
// Se recalcula la visibilidad/posición (`posicionarPanelContenido()`) en
// CADA cambio de selección real vía el nuevo callback `onSeleccionCambiada`
// que expone `crearPanelContenidoBloque` (ver su cabecera): antes de esta
// tarea el único disparador de recálculo tras un cambio de selección SIN
// reconstrucción completa era el propio `focusin` (registrado en
// `crearContenido()`, ver más abajo), que NO se dispara cuando un clic
// CIERRA un panel sobre un bloque que YA tenía el foco (no hay movimiento de
// foco que reordenar) -- sin este callback, `contenidoSlot` se habría
// quedado visualmente abierto tras ese cierre concreto hasta el próximo
// evento que sí recalculase la posición.

// --------------------------------------------------------------------------
// DECISIÓN 11 (revisada: petición directa del usuario, 2026-07-21 -- ver
// TSK-0151/DEC-058 punto 4 para el estado ANTERIOR, ya no vigente): el
// selector de ejercicios se reubica FUERA de la topbar, como hermano
// inmediato justo debajo de ella
// --------------------------------------------------------------------------
// TSK-0151 (DEC-058 punto 4) había puesto el selector DENTRO de la topbar,
// entre la marca y la pastilla de sesión. Petición directa posterior del
// usuario ("que aparezca el contenedor 'Ejercicios de diagramas' fuera del
// menú, debajo"): vuelve a salir de la topbar, pero NO regresa a ser una
// sección arbitraria en cualquier punto del editor -- se ancla como hermano
// inmediato de `topbar.elemento` en `raiz` (ver "raíz DOM de la composición"
// más abajo), así que sigue siendo lo primero que se ve tras la barra de
// marca/sesión, solo que ya no compite visualmente por espacio dentro de
// ella. `exercise-selector.js` (DEC-060/TSK-0147, "done", fuera de las rutas
// permitidas de esta tarea) mantiene su API pública 1:1: este cambio SOLO
// afecta a DÓNDE se inserta su `elemento`, nunca a cómo se construye ni a
// quién lo orquesta.
//
// Este controlador sigue sin importar `services/**`/`state/**` (misma regla
// que "DECISIÓN 9" para `usuario`/`onCerrarSesion`): recibe
// `selectorEjerciciosElemento` como un `HTMLElement | null` ya construido y
// orquestado por `diagram-editor-page.js` (que sigue siendo quien llama a
// `crearSelectorEjercicios`, `mostrarCargando`/`mostrarLista`/`mostrarError`
// y `destruir()` en el desmontaje). Este archivo nunca llama a ninguno de
// esos métodos ni lo destruye: es un dato de composición puramente opaco,
// igual de opaco que `usuario`/`onCerrarSesion` ya lo eran.
//
// Caso sin sesión/standalone (`usuario` es `null`): `crearTopbar` devuelve
// `null` y el test `diagram-editor-page.spec.js` (TSK-0085) EXIGE que en ese
// caso no exista ninguna `.diagram-editor-topbar` en el DOM. La composición
// de `raiz` (ver más abajo) inserta `selectorEjerciciosElemento`
// DIRECTAMENTE como hijo de `raiz`, justo antes de `titulo`, tanto si hay
// `topbar` (como su hermano siguiente) como si no (en ese caso es
// simplemente el primer hijo) -- un único punto de inserción, nunca se monta
// dos veces.
//
// Landmarks (accessibility-reviewer, WCAG 2.2): ni la topbar (un `<header>`
// sin rol de navegación) ni el `<section>` autónomo del selector
// (`exercise-selector.js`, "done") introducen ningún landmark de navegación
// (`<nav>`/`role="navigation"`) ni un segundo `role="banner"` -- el único
// `<nav>` de la aplicación sigue siendo el top-nav global, ajeno a este
// archivo.
// --------------------------------------------------------------------------

// --------------------------------------------------------------------------
// DECISIÓN 13 (TSK-0211, addendum de DEC-072/TSK-0209): declarar/editar los
// parámetros de una función, no solo los argumentos de su llamada
// --------------------------------------------------------------------------
// TSK-0209 cubrió `Llamada.argumentos` (lo que el LLAMADOR envía), pero
// `manejarSubmitNuevaFuncion` seguía sin ninguna forma de declarar
// `Funcion.parametros` (lo que la función en sí RECIBE): toda función nueva
// nacía con `parametros: []` para siempre, así que cualquier `Llamada` con
// argumentos fallaba en el backend ("la función espera 0 argumentos").
//
// Dos puntos de edición distintos, mismo modelo (`crearParametro`,
// editor-model.js, sin cambios -- `nombre`+`tipo`, `tipo` un valor de
// `TIPO_PRIMITIVO`, mismo enum/patrón de selector ya usado en
// `expression-editor.js` para el tipo de un literal, duplicado aquí como
// constante trivial exactamente igual que `PATRON_IDENTIFICADOR_FUNCION` más
// abajo -- ese módulo tampoco exporta su versión y está en rutas prohibidas):
//   1. En el momento de CREAR la función (`formularioNuevaFuncion`, ya
//      existente de TSK-0078): una lista de parámetros LOCAL, sin persistir
//      en `programa` hasta confirmar "Crear función" -- si se cancela el
//      formulario, la lista local se descarta sin dejar rastro.
//   2. Mientras se EDITA el diagrama de una función ya creada (pestaña
//      activa distinta de "Principal"): una sección nueva "Parámetros de
//      esta función" que persiste cada cambio de inmediato en
//      `programa.funciones[i].parametros` -- nunca hay un paso de "guardar"
//      aparte, mismo criterio que el resto de este controlador (todo cambio
//      lógico se aplica y autoguarda al instante).
//
// `editor-model.js` no necesitó ningún cambio (confirmado, no asumido): no
// existe ninguna función de "reemplazar Funcion por id" genérica, pero el
// patrón inmutable ya establecido por `actualizarCuerpoFuncion` (mismo
// archivo) se replica aquí tal cual para `parametros`
// (`actualizarParametrosFuncion`, más abajo, junto a `manejarCambioPrograma`)
// sin reabrir ese módulo (rutas_prohibidas de esta tarea).
//
// Factoría compartida (`crearListaEditableParametros`, más abajo, junto a
// `PATRON_IDENTIFICADOR_FUNCION`): mismo componente para los dos puntos de
// edición de arriba, parametrizado por `obtenerParametros`/`escribirParametros`
// (el primero lee el array vigente -- local o de `programa`--, el segundo lo
// reemplaza por completo de forma inmutable). Mismo patrón de
// añadir/quitar ya aprobado en `block-content-panel.js#crearCampoListaExpresiones`
// (TSK-0209): reconstruye su sub-DOM SOLO al cambiar el NÚMERO de filas
// (añadir/quitar), nunca al renombrar/cambiar tipo de una fila existente
// (evita perder el foco/cursor a mitad de escritura, mismo criterio que
// `crearCampoTexto` de ese módulo). Nombre por defecto de una fila nueva
// (`parametro1`, `parametro2`...) SIEMPRE válido y sin colisión, para no
// tener que permitir nunca un `Parametro.nombre` vacío en `programa`
// (`crearParametro` de editor-model.js exige una cadena no vacía) -- mismo
// criterio que `literalPorDefecto()` en `block-content-panel.js`: un valor
// por defecto ya válido, nunca un "placeholder inválido" transitorio.
//
// Gestión de foco (criterio de aceptación, WCAG 2.2 §2.4.3): añadir mueve el
// foco al campo de NOMBRE de la fila recién creada (el control que el
// alumno necesita rellenar de inmediato); quitar mueve el foco al botón
// "Añadir parámetro" (control estable, siempre presente, mismo criterio que
// `eliminarFuncionDiagrama`/`quitarArgumento` en el resto de este dominio:
// nunca cae a `document.body`).
// --------------------------------------------------------------------------

// --------------------------------------------------------------------------
// DECISIÓN 14 (TSK-0217, DEC-076, addendum de DECISIÓN 13): declarar el tipo
// de retorno de una función (`Funcion.tipo_retorno`)
// --------------------------------------------------------------------------
// El campo `Funcion.tipo_retorno` (`Tipo | None`, `None` = procedimiento) ya
// existe en el esquema IR (`ir.py`/`crearFuncion`, editor-model.js) y el
// motor de ejecución (`diagram_engine.py`, TSK-0215) ya lo usa correctamente
// para decidir si una `Llamada` con `variable_destino` (TSK-0216) puede
// capturar un valor de retorno -- pero, hasta esta tarea, NINGÚN punto de la
// UI permitía declararlo: toda función creada desde el editor quedaba
// `tipo_retorno: null` para siempre, aunque su cuerpo tuviera un `Retorno`
// con expresión real.
//
// Se añade el selector SOLO a la sección "Parámetros de esta función"
// (`seccionParametrosFuncion`, DECISIÓN 13), no también al formulario
// "Crear función nueva": toda función nace como procedimiento
// (`tipoRetorno` no se pasa a `crearFuncion` en `manejarSubmitNuevaFuncion`,
// que ya usa su valor por defecto `null`) y su pestaña queda activa nada
// más crearla (`diagramaActivo = funcion.id`, ver ese manejador), así que el
// selector de la sección post-creación ya es accesible de inmediato sin
// pasos adicionales. Duplicarlo también en el formulario de creación no
// añade ninguna función nueva (un valor elegido ahí sería idéntico a
// cambiarlo justo después en la sección que ya se muestra) y sí duplicaría
// lógica de renderizado/enum -- en contra del criterio explícito de esta
// ficha ("sin duplicar lógica").
//
// Selector (`crearSelectorTipoRetorno`, más abajo, junto a
// `crearSelectorTipoParametro`): mismo enum/mismas etiquetas
// `ETIQUETA_TIPO_PRIMITIVO_PARAMETRO` ya usadas por `Parametro.tipo`
// (TSK-0211), más una opción adicional "Procedimiento (no devuelve nada)"
// que aplica `null` -- mismo patrón de sentinel opcional ya usado por
// `Entrada.tipo` (`crearCampoTipoPrimitivoOpcional`, TSK-0212,
// `block-content-panel.js`, rutas_prohibidas de esta tarea: no se importa
// nada de ese módulo, se replica aquí el mismo criterio de sentinel con una
// constante propia, `VALOR_TIPO_RETORNO_PROCEDIMIENTO`).
//
// Persistencia (`actualizarTipoRetornoFuncion`, editor-model.js: a
// diferencia de TSK-0211, esta tarea SÍ tiene `editor-model.js` en sus
// rutas_permitidas, así que la operación de reemplazo inmutable por `id`
// vive allí -- mismo patrón que `actualizarCuerpoFuncion`, ya existente en
// ese módulo -- y no se duplica en este archivo): persiste de inmediato en
// `programa.funciones[i].tipo_retorno`, sin ningún paso de "guardar" aparte,
// mismo criterio que el resto de este controlador. `actualizarSeccionParametrosFuncion`
// (DECISIÓN 13) sincroniza también el valor de este selector cada vez que
// cambia la función activa.
// --------------------------------------------------------------------------

import {
  crearPrograma,
  crearFuncion,
  crearParametro,
  agregarFuncion,
  actualizarCuerpoFuncion,
  actualizarTipoRetornoFuncion,
  eliminarFuncion,
  TIPO_PRIMITIVO,
} from './editor-model.js';
import { crearEstadoVisual, estadoVisualAObjeto, establecerPosicion } from './editor-visual-state.js';
import { serializarPrograma, deserializarPrograma } from './ir-serialization.js';
import { crearVistaPaleta, definicionDeTipo } from './palette-view.js';
import { crearVistaCanvas } from './canvas-view.js';
// `block-operations.js` (TSK-0024) YA NO se importa aquí: ver "DECISIÓN 4"
// en la cabecera de este archivo (TSK-0061/DEC-027) — su panel queda
// sustituido por la papelera + Supr/Backspace + "duplicar" de
// `canvas-view.js`. El archivo `block-operations.js` no se toca ni se
// borra (fuera de las rutas permitidas de TSK-0061).
// `connectors.js` (TSK-0025) YA NO se importa aquí: ver "DECISIÓN 5" en la
// cabecera de este archivo (TSK-0062/DEC-027) — con el modelo de lista
// vertical la conexión es el orden, sin acción independiente de
// "conectar"/"desconectar". El archivo `connectors.js` no se toca ni se
// borra (fuera de las rutas permitidas de TSK-0062).
import { crearPanelContenidoBloque } from './block-content-panel.js';
import { crearPanelEjecucion } from './execution-panel.js';
// Séptimo slot (TSK-0124, CAP-STRUCT-CORRECTION-OUTPUT-DIAGRAM, DEC-041/
// DEC-051): panel "Autocorregir ejercicio", hermano de `execution-panel.js`
// (ver la cabecera de `autocorrection-panel.js`, "De dónde salen
// ejercicioId/csrfToken", para el porqué de que este controlador siga sin
// importar `state/**`/`services/**` directamente pese a montar este panel).
import { crearPanelAutocorreccion } from './autocorrection-panel.js';
// Ventana emergente accesible reutilizable (petición del usuario: "Crear
// función nueva" pasa a mostrarse en un modal). `portal: false` -- el diálogo
// se mantiene dentro de la raíz del editor (este editor no tiene ancestros con
// `transform`/`contain` que rompan `position: fixed`).
import { crearModalDialogo } from '../../components/modal-dialog.js';
import { crearIcono } from '../../components/base/iconos.js';
import {
  cargarProgramaLocal,
  guardarProgramaLocal,
  crearAutoguardadoLocal,
} from './editor-persistence.js';
// Registro de la hoja de estilo del panel de contenido (TSK-0050/TSK-0051):
// ver la nota de integración en la cabecera de `block-content-panel.css`,
// que anticipa explícitamente este `import` aquí, en vez de en `index.js`
// (que no está en las rutas permitidas de esta tarea). El resto de hojas de
// estilo de la feature se siguen registrando en `index.js`; esta es la
// única excepción documentada y justificada por el alcance de TSK-0051.
import './block-content-panel.css';
// Hoja de estilo del editor de expresión (técnico y vista guiada), que el panel
// de contenido monta dentro de sus campos. Mismo motivo/excepción que la hoja
// anterior: `index.js` no está en las rutas permitidas y este es el punto real
// donde el widget aparece en pantalla, así que se registra junto a su panel.
import './expression-editor.css';
// Mismo motivo/excepción que la hoja anterior, ahora para el sexto slot
// (TSK-0058, panel "Ejecutar diagrama"): `index.js` no está en las rutas
// permitidas de esta tarea.
import './execution-panel.css';
// Mismo motivo/excepción, ahora para el séptimo slot (TSK-0124, panel
// "Autocorregir ejercicio"): `index.js` no está en las rutas permitidas de
// esta tarea.
import './autocorrection-panel.css';
// Mismo motivo/excepción, ahora para el control de tipo de retorno (TSK-0217,
// "DECISIÓN 14"): `index.js` no está en las rutas permitidas de esta tarea,
// así que la hoja de estilo propia de este archivo (`editor-controller.css`,
// la primera desde TSK-0124) se registra aquí, no allí.
import './editor-controller.css';

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 * @typedef {import('./editor-visual-state.js').EstadoVisual} EstadoVisual
 */

let contadorInstancias = 0;

/**
 * El motivo del bloqueo de edición (2026-08-07): el mismo en la paleta y en
 * cada cambio que se rechaza por él (DEC-180).
 */
const MENSAJE_EDICION_BLOQUEADA = 'Pulsa "Iniciar intento" para empezar a construir tu solución de este ejercicio.';

/**
 * Datos de un control del canvas que lo identifican entre dos
 * reconstrucciones (DEC-180): el bloque, el puerto o la flecha.
 */
const DATOS_QUE_IDENTIFICAN = /** @type {const} */ (['nodeId', 'portNode', 'portValue', 'arcId', 'arcEndpoint']);

/**
 * @param {HTMLElement} elemento
 * @returns {string}
 */
function nombreDeControl(elemento) {
  return elemento.getAttribute('aria-label') ?? (elemento.textContent ?? '').trim();
}

/**
 * @param {HTMLElement} vistaElemento
 * @returns {HTMLElement | null}
 */
function encabezadoDe(vistaElemento) {
  return vistaElemento.querySelector('h2');
}

/**
 * Mueve el foco del documento a `elemento`, dándole `tabindex="-1"` primero
 * si hace falta (destino programático de foco, no parte del orden de
 * tabulación normal — mismo patrón que las guías WAI-ARIA APG de gestión de
 * foco tras cambios de contenido). Nunca asigna un `tabindex` positivo
 * (docs/03-frontend.md §13.1).
 * @param {HTMLElement} elemento
 */
function moverFocoA(elemento) {
  if (!elemento.hasAttribute('tabindex')) {
    elemento.setAttribute('tabindex', '-1');
  }
  elemento.focus();
}

/**
 * Extrae hasta dos iniciales en mayúscula de un nombre visible, para el
 * avatar puramente decorativo de la cabecera de sesión (TSK-0085, "DECISIÓN
 * 9"). Nunca es la única fuente de la identidad del usuario en el DOM: el
 * texto real (nombre completo + rol) vive siempre en un nodo de texto
 * aparte, ver `crearTopbar`.
 * @param {string} nombreVisible
 * @returns {string}
 */
function obtenerIniciales(nombreVisible) {
  const partes = nombreVisible.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '';
  const primera = partes[0]?.[0] ?? '';
  const segunda = partes.length > 1 ? (partes[1]?.[0] ?? '') : '';
  return (primera + segunda).toUpperCase();
}

/**
 * Nombre del editor en su barra de marca y, en el rediseño (Fase 4b), la
 * etiqueta del panel Ejercicios que pinta la página (`sinCabecera`). Una sola
 * fuente para los dos sitios.
 */
export const MARCA_EDITOR_DIAGRAMAS = 'Flujo · Editor de diagramas';

/** Guía de aprendizaje de este editor (mismos dos sitios que la marca). */
export const RUTA_GUIA_DIAGRAMAS = '/guia-diagramas.html';

/**
 * Cabecera del editor (TSK-0085, "DECISIÓN 9" en la cabecera del archivo):
 * marca "Flujo · Editor de diagramas". Con `usuario` incluía también la
 * pastilla de usuario + botón "Cerrar sesión"; esa sesión se ha unificado en
 * el top-nav superior (`components/session-menu.js`, montado por
 * `pages/shell-nav.js`), presente en todas las landing. Por eso
 * `montarPaginaEditor` pasa siempre `usuario: null` y esta función devuelve
 * una topbar con SOLO la marca. Cuando SÍ se pasa `usuario` (consumidores
 * directos/tests), conserva el comportamiento previo (marca + pastilla +
 * logout), sin cambios en canvas/nodos/conexiones/serialización.
 *
 * Ningún dato de sesión sensible (id, email, token, csrf) se escribe en
 * ningún atributo/dataset del DOM: solo `nombreVisible`/`rol`, como texto
 * visible -- exactamente lo estrictamente necesario para mostrar la
 * identidad (criterio de aceptación de la ficha).
 * @param {{ nombreVisible: string, rol: string } | null} usuario
 * @param {(() => void | Promise<void>) | null} onCerrarSesion
 * @returns {{ elemento: HTMLElement, destruir: () => void }}
 */
function crearTopbar(usuario, onCerrarSesion) {
  const topbar = document.createElement('header');
  topbar.className = 'diagram-editor-topbar';

  const marca = document.createElement('span');
  marca.className = 'diagram-editor-topbar-brand';

  const marcaIcono = document.createElement('span');
  marcaIcono.className = 'diagram-editor-topbar-brand-mark';
  marcaIcono.setAttribute('aria-hidden', 'true');
  // Rediseño (Fase 4b): icono de trazo en vez del emoji.
  marcaIcono.append(crearIcono('codigo'));

  // Mockup aprobado (imagen de referencia, 2026-07-20): "Flujo · Editor de
  // diagramas" en vez de solo "Editor de diagramas" -- el texto completo
  // sigue siendo el nombre accesible real de este `<span>` (nunca solo el
  // icono decorativo de arriba, que lleva `aria-hidden`).
  marca.append(marcaIcono, document.createTextNode(MARCA_EDITOR_DIAGRAMAS));

  // Enlace a la guía de aprendizaje (`guia-diagramas.html`, 2026-08-05),
  // alineado al borde final de la barra. Es el equivalente EXACTO del que ya
  // tiene el editor de pseudocódigo en su propia barra de marca
  // (`features/pseudocode-editor/brand-bar.js`), a petición del usuario: la
  // guía se busca donde ya está en la otra pestaña.
  //
  // Va aquí y no en la página porque esta barra es exclusiva de esta feature.
  // No necesita `margin-inline-start: auto` como la de pseudocódigo: esta
  // topbar ya es `justify-content: space-between`, así que un segundo hijo se
  // coloca solo al final (ver `diagram-editor.css`).
  //
  // Se añade ANTES de la bifurcación por `usuario` para que exista en los dos
  // casos. Es el único elemento interactivo que esta barra vuelve a tener: no
  // toca el canvas, ni los nodos, ni el modelo, ni la serialización.
  const enlaceGuia = document.createElement('a');
  enlaceGuia.className = 'diagram-editor-topbar-guide-link';
  enlaceGuia.href = RUTA_GUIA_DIAGRAMAS;
  // Rediseño (Fase 4b): libro de trazo en vez del emoji (ya `aria-hidden`).
  enlaceGuia.append(crearIcono('libro'), document.createTextNode(' Guía de aprendizaje'));

  // Sin `usuario`, la topbar conserva SOLO la marca. La pastilla de usuario +
  // botón "Cerrar sesión" se han unificado en el top-nav superior
  // (`components/session-menu.js`, montado por `pages/shell-nav.js`), presente
  // en todas las landing -- por eso esta cabecera ya no las construye. Se
  // mantiene la marca (encabezado "Flujo · Editor de diagramas") para no perder
  // el sub-encabezado del editor. El canvas, los nodos, las conexiones, el
  // modelo de datos y la serialización no se tocan.
  if (!usuario) {
    topbar.append(marca, enlaceGuia);
    return {
      elemento: topbar,
      destruir: () => {},
    };
  }

  const pill = document.createElement('span');
  pill.className = 'diagram-editor-user-pill';

  const avatar = document.createElement('span');
  avatar.className = 'diagram-editor-user-avatar';
  avatar.setAttribute('aria-hidden', 'true');
  avatar.textContent = obtenerIniciales(usuario.nombreVisible);

  const texto = document.createElement('span');
  texto.className = 'diagram-editor-user-text';
  // Nombre accesible real de la pastilla (criterio de aceptación: "no
  // decorativa, texto real o aria-label"): este `textContent` YA es
  // suficiente, no hace falta duplicarlo en un `aria-label` del contenedor.
  texto.textContent = usuario.rol ? `${usuario.nombreVisible} · ${usuario.rol}` : usuario.nombreVisible;

  pill.append(avatar, texto);

  const boton = document.createElement('button');
  boton.type = 'button';
  boton.className = 'diagram-editor-logout-button';
  boton.textContent = 'Cerrar sesión';

  let procesando = false;
  const manejarClick = () => {
    if (procesando || !onCerrarSesion) return;
    procesando = true;
    boton.disabled = true;
    boton.textContent = 'Cerrando sesión…';
    // `onCerrarSesion` (construido por `diagram-editor-page.js`) ya absorbe
    // cualquier fallo de red y SIEMPRE redirige (ver "DECISIÓN 9"): este
    // `catch`/`finally` es una red de seguridad puramente defensiva para no
    // dejar una promesa rechazada sin manejar ni el botón deshabilitado para
    // siempre si esa garantía cambiase alguna vez.
    Promise.resolve(onCerrarSesion())
      .catch(() => {})
      .finally(() => {
        procesando = false;
        boton.disabled = false;
        boton.textContent = 'Cerrar sesión';
      });
  };
  boton.addEventListener('click', manejarClick);

  // "DECISIÓN 11" (revisada): el selector de ejercicios YA NO vive dentro de
  // esta topbar (ver la cabecera del archivo) -- esta barra vuelve a
  // contener solo marca + pastilla de sesión + botón, exactamente igual que
  // antes de TSK-0151.
  topbar.append(marca, enlaceGuia, pill, boton);

  return {
    elemento: topbar,
    destruir: () => {
      boton.removeEventListener('click', manejarClick);
    },
  };
}

/**
 * Crea el controlador de composición del editor: un único `programa` +
 * `estadoVisual` sincronizado entre paleta, canvas, contenido de bloque,
 * ejecución y autoguardado local (paneles de operaciones y conectores
 * retirados, ver "DECISIÓN 4"/"DECISIÓN 5"). Ver cabecera del archivo para
 * las decisiones de diseño (contrato de selección) y el porqué de
 * "reconstruir en vez de mutar in place".
 *
 * Reglas no negociables (docs/03-frontend.md §4, §13, §16.5, ver también
 * ficha de esta tarea): funciona SIN cuenta (autoguardado local vía
 * `editor-persistence.js`); no importa `services/*` ni llama a `fetch`.
 * TSK-0124 (panel "Autocorregir", séptimo slot): esta regla se mantiene
 * intacta a nivel de IMPORTS -- `ejercicioId`/`csrfToken` llegan como datos
 * opacos ya resueltos por la página (ver la cabecera de
 * `autocorrection-panel.js`, "De dónde salen ejercicioId/csrfToken"), este
 * archivo se limita a reenviarlos sin interpretarlos, exactamente igual que
 * ya hace con `usuario`/`onCerrarSesion`.
 *
 * @param {{
 *   usuario?: { id?: string, nombreVisible: string, rol: string } | null,
 *   onCerrarSesion?: (() => void | Promise<void>) | null,
 *   ejercicioId?: string | null,
 *   csrfToken?: string | null,
 *   ambitoBorrador?: string,
 *   ejercicioCompletado?: boolean,
 *   onEjercicioCompletado?: () => void,
 *   selectorEjerciciosElemento?: HTMLElement | null,
 *   dashboardElemento?: HTMLElement | null,
 *   sinCabecera?: boolean,
 * }} [opciones] Rediseño (Fase 4b): `sinCabecera` deja fuera la barra de
 *   marca («Flujo · Editor de diagramas» + guía) porque la página la pinta
 *   como cabecera del panel Ejercicios de su HUD (`MARCA_EDITOR_DIAGRAMAS`,
 *   `RUTA_GUIA_DIAGRAMAS`). Por defecto `false`: quien monte el editor por su
 *   cuenta la sigue teniendo. TSK-0085, "DECISIÓN 9": cabecera de sesión opcional. Sin
 *   `usuario` (valor por defecto), el controlador se comporta exactamente
 *   igual que antes de esta tarea, sin ninguna cabecera montada. `usuario.id`
 *   (cuando existe) namespacea el autoguardado local por cuenta, ver
 *   `editor-persistence.js#claveAlmacenamiento`. TSK-0124: `ejercicioId`
 *   (UUID del ejercicio cargado, `null` si el editor se usa standalone o
 *   mientras el enunciado todavía no ha resuelto, ver
 *   `establecerEjercicioActual` más abajo) y `csrfToken` (token CSRF
 *   vigente, `''`/`null` si no hay sesión) se reenvían tal cual al panel de
 *   autocorrección. "DECISIÓN 11" (revisada): `selectorEjerciciosElemento`
 *   es el `elemento` OPACO del selector de ejercicios (`exercise-
 *   selector.js`, DEC-060), ya construido y orquestado por
 *   `diagram-editor-page.js`; este controlador no lo interpreta, solo lo
 *   inserta en `raiz` como hermano inmediato de la topbar (o como primer
 *   hijo si no hay `usuario`/topbar). "DECISIÓN 12" (petición directa del
 *   usuario, 2026-07-21): `dashboardElemento` es el `elemento` OPACO del
 *   dashboard de enunciado+puntuación (`diagram-editor-page.js`, antes
 *   montado por la PÁGINA como hermano anterior a todo el editor, quedando
 *   por encima de la topbar/selector -- el usuario pidió que quedase DEBAJO
 *   de "Ejercicios de diagramas"). Mismo patrón exactamente que
 *   `selectorEjerciciosElemento`: este controlador no lo interpreta, solo lo
 *   inserta en `raiz` justo DESPUÉS de `selectorEjerciciosElemento` (o de la
 *   topbar, si no hay selector; o como primer hijo, si no hay ninguno de los
 *   dos) y ANTES de `titulo`.
 * @returns {{
 *   elemento: HTMLElement,
 *   enfocarInicial: () => void,
 *   destruir: () => void,
 *   obtenerPrograma: () => NodoModelo,
 *   obtenerEstadoVisual: () => EstadoVisual,
 *   establecerEjercicioActual: (ejercicioId: string | null, material?: import('./autocorrection-panel.js').MaterialEjercicio) => void,
 *   establecerEjercicioCompletado: (completado: boolean) => void,
 * }}
 */
export function crearControladorEditor(opciones = {}) {
  const {
    usuario = null,
    onCerrarSesion = null,
    selectorEjerciciosElemento = null,
    dashboardElemento = null,
    sinCabecera = false,
  } = opciones;
  // TSK-0124: ver el docstring de arriba y la cabecera de
  // `autocorrection-panel.js`. `ejercicioIdActual` es MUTABLE (a diferencia
  // de `usuario`/`onCerrarSesion`) porque la carga del ejercicio
  // (`obtenerEjercicioPorSlug`, TSK-0054) es asíncrona respecto al montaje
  // de este controlador: la página no conoce el UUID todavía en el instante
  // de esta llamada, solo tras resolver esa petición (ver
  // `establecerEjercicioActual`, más abajo). `csrfToken`, en cambio, SÍ se
  // conoce de forma síncrona en este punto (la sesión ya se comprobó antes
  // de montar, `arrancarPaginaEditor` en `diagram-editor-page.js`), así que
  // no necesita un setter propio.
  let ejercicioIdActual = typeof opciones.ejercicioId === 'string' ? opciones.ejercicioId : null;
  // Material pedagógico PÚBLICO del ejercicio (`contenido_pedagogico`,
  // DEC-015), que llega junto al id por `establecerEjercicioActual`. Solo se
  // reenvía al panel de autocorrección para su ayuda graduada (§10): no
  // influye en el canvas, ni en el programa, ni en la ejecución.
  /** @type {import('./autocorrection-panel.js').MaterialEjercicio} */
  let materialEjercicioActual = {};
  /** ¿El alumno ya acertó el ejercicio que se está trabajando? Ver
   * `crearAutocorreccion`: sobrevive a las reconstrucciones del panel. */
  let ejercicioCompletadoActual = opciones.ejercicioCompletado === true;
  /** ¿Hay un intento abierto ahora mismo? Lo informa el panel de corrección. */
  let hayIntentoAbiertoActual = false;
  const csrfTokenActual = typeof opciones.csrfToken === 'string' ? opciones.csrfToken : '';
  // Marca de tiempo FIJA para el indicador de tiempo del panel de
  // autocorrección (DEC-041/DEC-051, ver la cabecera de
  // `autocorrection-panel.js`, "Indicador de tiempo"): tomada UNA ÚNICA VEZ
  // aquí, nunca en `crearAutocorreccion()` (que se invoca de nuevo en cada
  // reconstrucción, ver `reconstruirDependientes()`) -- si se tomara ahí, el
  // cronómetro se reiniciaría a 0 en cada pulsación de tecla del panel de
  // contenido.
  const tiempoInicioMs = Date.now();

  contadorInstancias += 1;
  const idInstancia = contadorInstancias;

  // ---- estado central (única fuente de verdad de toda la composición) ----
  // El autoguardado se namespacea por `usuario.id` (bug corregido: antes la
  // clave de `localStorage` era fija y global, así que el diagrama de un
  // usuario se filtraba al siguiente que iniciase sesión en el mismo
  // navegador, ver `editor-persistence.js#claveAlmacenamiento`).
  const usuarioId = usuario?.id ?? null;
  // Ámbito del borrador: el ejercicio que se está haciendo, o el borrador
  // libre si no hay ninguno (ver `state/ambito-borrador.js`). Antes había UN
  // solo diagrama por cuenta: traducir desde Python machacaba el del
  // ejercicio en curso, y los cinco ejercicios compartían lienzo. Es FIJO
  // durante la vida de esta instancia; cambiar de ejercicio remonta el
  // editor (`diagram-editor-page.js`), que es lo que carga el otro borrador.
  const ambitoBorrador = typeof opciones.ambitoBorrador === 'string' ? opciones.ambitoBorrador : undefined;
  const restaurado = cargarProgramaLocal(usuarioId, ambitoBorrador);
  // `cargarProgramaLocal` tipa `programa` como `unknown` (no puede saber en
  // tiempo de compilación que su propio `deserializarPrograma` interno
  // devuelve un `Programa` válido, ver editor-persistence.js): el cast
  // explícito documenta esa garantía ya asumida por ese módulo (TSK-0026),
  // no introduce una comprobación nueva.
  /** @type {NodoModelo} */
  let programa = /** @type {NodoModelo} */ (restaurado?.programa) ?? crearPrograma();
  // Referencia ÚNICA y ESTABLE durante toda la vida del controlador: se
  // pasa por referencia a canvas/operaciones (nunca se sustituye por un
  // objeto nuevo), así que mover un bloque no exige reconstruir nada.
  const estadoVisual = restaurado?.estadoVisual ?? crearEstadoVisual();

  // Deshacer GENERAL (petición directa del usuario, mockup aprobado): pila
  // de snapshots (programa serializado + estado visual) tomados ANTES de
  // cada mutación LÓGICA confirmada (colocar/editar contenido/eliminar/
  // reordenar/crear función/eliminar función -- todo lo que pasa por
  // `manejarCambioPrograma`, ver ahí). Deliberadamente NO cubre el arrastre
  // libre de un bloque (mover no toca la IR, "su orden en el programa no
  // cambia" según su propio anuncio ya existente en canvas-view.js):
  // alcance acotado a mutaciones lógicas, documentado en vez de asumido en
  // silencio (CLAUDE.md). Las ediciones de contenido CONSECUTIVAS (una
  // notificación por tecla, ver DECISIÓN 10) se COALESCEN en un único paso
  // de deshacer mientras no se interrumpan por una acción de otro origen:
  // sin esto, deshacer solo revertiría el último carácter escrito.
  const LIMITE_HISTORIAL_DESHACER = 20;
  /** @type {Array<{ programaJson: unknown, estadoVisualObj: Record<string, import('./editor-visual-state.js').PosicionVisual> }>} */
  let historialDeshacer = [];
  /** @type {'canvas' | 'contenido' | null} */
  let origenAnteriorParaHistorial = null;

  /** @type {'canvas' | 'contenido' | null} */
  let origenUltimoCambio = null;
  /** @type {string | null} Id del bloque a re-enfocar tras la próxima
   * reconstrucción (p.ej. el que se acaba de colocar desde la paleta). */
  let idParaEnfocar = null;
  let reconstruccionPendiente = false;

  // TSK-0078 ("DECISIÓN 7"): diagrama actualmente mostrado en el canvas.
  // `'principal'` o el `id` de una `Funcion` de `programa.funciones`.
  /** @type {'principal' | string} */
  let diagramaActivo = 'principal';

  // TSK-0080 ("DECISIÓN 8"): id de la `Funcion` cuya pestaña está mostrando
  // el paso de confirmación de borrado ("Confirmar eliminar X"/"Cancelar"),
  // o `null` si ninguna. Nunca aplica a `'principal'` (no es un id de
  // `programa.funciones`).
  /** @type {string | null} */
  let idPendienteConfirmarEliminarFuncion = null;

  /** @returns {NodoModelo[]} */
  function obtenerFunciones() {
    return /** @type {NodoModelo[]} */ (programa.funciones ?? []);
  }

  /** @returns {NodoModelo | null} La `Funcion` activa, o `null` si se está viendo "Principal". */
  function funcionActiva() {
    if (diagramaActivo === 'principal') return null;
    return obtenerFunciones().find((funcion) => funcion.id === diagramaActivo) ?? null;
  }

  /**
   * Programa que reciben `canvas-view.js`/`block-content-panel.js` (ver
   * "DECISIÓN 7" en la cabecera del archivo): el `programa` central tal
   * cual si se está viendo "Principal", o un Programa envoltorio SINTÉTICO
   * con el `cuerpo` sustituido por el de la función activa. Nunca se usa
   * para ejecución ni autoguardado (ambos usan siempre `programa`
   * directamente).
   * @returns {NodoModelo}
   */
  function programaParaVista() {
    const funcion = funcionActiva();
    if (!funcion) return programa;
    return { ...programa, cuerpo: /** @type {NodoModelo} */ (funcion.cuerpo) };
  }

  // ---- raíz DOM de la composición ----
  const raiz = document.createElement('div');
  raiz.className = 'diagram-editor';

  // TSK-0085 ("DECISIÓN 9"): cabecera de sesión opcional, primer elemento
  // visual/de tabulación de la composición (mockup aprobado: barra superior
  // con marca + pastilla de usuario a la derecha).
  const topbar = sinCabecera ? null : crearTopbar(usuario, onCerrarSesion);

  const titulo = document.createElement('h1');
  titulo.className = 'diagram-editor-heading';
  titulo.id = `diagram-editor-heading-${idInstancia}`;
  titulo.textContent = 'Editor visual de diagramas';

  const layout = document.createElement('div');
  layout.className = 'diagram-editor-layout';

  // TSK-0078 ("DECISIÓN 7"): navegación entre "Principal" y el diagrama de
  // cada función, en su propia fila a todo lo ancho por encima de
  // paleta+canvas (ver diagram-editor.css, área de grid 'diagrams').
  const diagramasSlot = document.createElement('div');
  diagramasSlot.className = 'diagram-editor-slot diagram-editor-slot-diagrams';
  const paletaSlot = document.createElement('div');
  paletaSlot.className = 'diagram-editor-slot diagram-editor-slot-palette';
  const canvasSlot = document.createElement('div');
  canvasSlot.className = 'diagram-editor-slot diagram-editor-slot-canvas';
  // TSK-0088 ("DECISIÓN 10"): listener de `focusin` ÚNICO y ESTABLE durante
  // toda la vida del controlador (`canvasSlot` nunca se destruye), que
  // recalcula el anclaje del panel de contenido ante cualquier cambio de
  // foco dentro del canvas VIGENTE en cada momento (delegación: no importa
  // qué instancia concreta de `canvasVista` esté montada dentro en este
  // instante). Antes de esta tarea se registraba de nuevo en cada
  // `crearContenido()` sobre `canvasVista.elemento` (que SÍ se destruye en
  // cada reconstrucción de canvas); ya no puede vivir ahí porque
  // `crearContenido()` deja de ejecutarse en las reconstrucciones de origen
  // `'contenido'` (ver `reconstruirDependientes()`), mientras que
  // `canvasVista` SÍ se sigue reconstruyendo en todas ellas -- registrarlo
  // sobre el elemento efímero acumularía listeners huérfanos o se perdería
  // por completo tras la primera reconstrucción de contenido.
  const manejarFocusInCanvasSlot = () => sincronizarModalContenido();
  canvasSlot.addEventListener('focusin', manejarFocusInCanvasSlot);
  // `operacionesSlot` (panel de operaciones, `block-operations.js`) RETIRADO
  // en TSK-0061 (ver "DECISIÓN 4" en la cabecera): la papelera y
  // Supr/Backspace de `canvas-view.js` ya viven DENTRO de `canvasSlot`, no
  // necesitan un slot propio.
  // `conectoresSlot` (panel de conectores, `connectors.js`) RETIRADO en
  // TSK-0062 (ver "DECISIÓN 5" en la cabecera): con la lista vertical, la
  // posición ES la conexión; no queda ninguna acción de
  // "conectar"/"desconectar" que necesite un slot propio.
  // El contenido del bloque seleccionado se muestra ahora en un MODAL centrado
  // (petición del usuario) en vez de un panel anclado junto al bloque. El
  // `elemento` del panel (`block-content-panel.js`, cuya lógica NO se toca) se
  // inserta en el cuerpo de este modal en `crearContenido()`. La apertura/
  // cierre y el título los gestiona `sincronizarModalContenido()` a partir de
  // la selección del propio panel (`onSeleccionCambiada`). El overlay bloquea
  // el fondo (accesibilidad: el contenido de detrás no es interactuable) y
  // atrapa el foco.
  //
  // `devolverFoco: false`: el retorno de foco al bloque lo gestiona ESTE
  // archivo (`cerrarModalContenidoDesdeUsuario`), no el componente. El panel
  // ABRE su modal al recibir foco un bloque (`focusin`); si el modal devolviera
  // el foco al bloque al cerrarse, ese `focusin` lo REABRIRÍA en bucle. Por eso
  // el cierre por el usuario se hace con la reapertura SUPRIMIDA
  // (`suprimirAperturaModalContenido`) mientras se devuelve el foco al bloque.
  let suprimirAperturaModalContenido = false;

  const modalContenido = crearModalDialogo({
    titulo: 'Contenido del bloque',
    devolverFoco: false,
    // X / Escape / clic fuera: se enruta por el mismo cierre unificado.
    onCerrar: () => cerrarModalContenidoDesdeUsuario(),
  });
  modalContenido.elemento.classList.add('block-content-panel-modal');

  // Descripción breve (cabecera del cuerpo), reutilizando la de la paleta si
  // existe para ese tipo de bloque; oculta si no hay descripción.
  const descripcionContenido = document.createElement('p');
  descripcionContenido.className = 'block-content-panel-modal-descripcion';
  descripcionContenido.hidden = true;

  const botonCerrarContenido = document.createElement('button');
  botonCerrarContenido.type = 'button';
  botonCerrarContenido.className = 'block-content-panel-modal-cerrar';
  botonCerrarContenido.textContent = 'Cerrar';
  botonCerrarContenido.addEventListener('click', () => cerrarModalContenidoDesdeUsuario());
  modalContenido.acciones.append(botonCerrarContenido);

  /**
   * Cierre del modal de contenido por acción explícita del usuario (X,
   * Escape, clic fuera o botón "Cerrar"). Cierra el modal, deselecciona el
   * bloque en el panel (estado coherente) y DEVUELVE el foco al bloque que lo
   * abrió, todo con la reapertura suprimida para que el `focusin` resultante
   * de ese retorno de foco no vuelva a abrir el modal. Tras esto el bloque
   * queda enfocado y sin selección; una nueva activación (clic/Enter) lo
   * reabre con normalidad.
   */
  function cerrarModalContenidoDesdeUsuario() {
    const idBloque = contenidoVista?.obtenerIdSeleccionadoEnCanvas() ?? null;
    suprimirAperturaModalContenido = true;
    if (modalContenido.estaAbierto()) modalContenido.cerrar();
    if (idBloque && canvasVista) {
      const bloque = /** @type {HTMLElement | null} */ (
        canvasVista.elemento.querySelector(`[data-node-id="${idBloque}"]`)
      );
      // `preventScroll: true` (coherente con la corrección del salto de scroll):
      // devolver el foco al bloque que abrió el modal no debe desplazar la página.
      if (bloque && typeof bloque.focus === 'function') bloque.focus({ preventScroll: true });
    }
    contenidoVista?.seleccionarBloque(null);
    suprimirAperturaModalContenido = false;
  }

  // Etiqueta/descripción legibles por tipo de bloque para la cabecera del
  // modal: las MISMAS de la paleta, sin inventar textos nuevos, por
  // `definicionDeTipo` (palette-view.js), que también usa el lienzo (DEC-180).

  const ejecucionSlot = document.createElement('div');
  ejecucionSlot.className = 'diagram-editor-slot diagram-editor-slot-execution';
  // Séptimo slot (TSK-0124, REUBICADO por TSK-0152/DEC-058 punto 5): panel
  // "Autocorregir ejercicio" -- mismo mecanismo de slot fijo que el resto,
  // nunca se mueve de contenedor (a diferencia de `contenidoSlot`/
  // "DECISIÓN 6", que se ancla al bloque seleccionado; este panel no tiene
  // ese requisito). Ya NO vive en su propia fila DEBAJO de "Ejecutar": el
  // usuario pidió el botón JUNTO a donde se crean las funciones (fila
  // `diagrams`) y JUSTO ENCIMA del canvas. El grid (`diagram-editor.css`,
  // área `autocorrection`) lo posiciona visualmente entre `diagrams` y
  // `palette canvas`; el orden DOM se alinea abajo en `layout.append(...)`
  // para que el orden de TABULACIÓN siga el orden visual (WCAG 2.2 SC
  // 1.3.2/2.4.3): diagrams -> autocorrección -> paleta -> canvas ->
  // ejecución.
  const autocorreccionSlot = document.createElement('div');
  autocorreccionSlot.className = 'diagram-editor-slot diagram-editor-slot-autocorrection';

  // Reorganización vertical (petición del usuario): `autocorreccionSlot` ya
  // NO vive dentro del grid `layout` -- se coloca en la fila "Ejercicios +
  // Autocorregir" (`.diagram-editor-exercise-row`, ver más abajo), a la
  // derecha del selector de ejercicios. El grid `layout` queda solo con el
  // ÁREA "Editor visual de diagramas": las pestañas de diagrama (function
  // tabs) a todo el ancho, luego paleta + canvas, y ejecución debajo.
  //
  // El contenido del bloque seleccionado ya NO se ancla dentro de `canvasSlot`
  // (antes `contenidoSlot`): vive en `modalContenido` (creado arriba), que se
  // añade a `raiz` una única vez y se portala a `document.body` al abrirse.
  // `canvasSlot` sigue siendo el `canvasRoot` del panel (delegación de eventos
  // de selección de bloques), sin cambios.
  layout.append(diagramasSlot, paletaSlot, canvasSlot, ejecucionSlot);
  raiz.append(modalContenido.elemento);

  // `topbar` siempre existe ahora: con `usuario` incluye pastilla+logout, sin
  // `usuario` conserva solo la marca (ver `crearTopbar`; la sesión se muestra
  // en el top-nav superior). Se añade como primer hijo de `raiz`.
  //
  // "DECISIÓN 11" (revisada): `selectorEjerciciosElemento` -- si se
  // proporcionó -- se añade siempre a continuación, como hermano inmediato de
  // la topbar (justo debajo de la barra). Único punto de inserción posible:
  // nunca se monta dentro de `topbar` (ver `crearTopbar`) ni dos veces.
  if (topbar) {
    raiz.append(topbar.elemento);
  }

  // Fila 2 (petición del usuario): "Ejercicios de diagramas" (el selector, que
  // ocupa el espacio principal) + "Autocorregir ejercicio" (`autocorreccionSlot`,
  // a su derecha), lado a lado y alineados por arriba. Ambos ocupan, en
  // conjunto, todo el ancho del contenedor. En pantallas estrechas se apilan
  // (ver `.diagram-editor-exercise-row` en `diagram-editor.css`). Si no hay
  // selector (uso standalone) la fila solo contiene la autocorrección.
  const filaEjercicio = document.createElement('div');
  filaEjercicio.className = 'diagram-editor-exercise-row';
  if (selectorEjerciciosElemento) {
    filaEjercicio.append(selectorEjerciciosElemento);
  }
  filaEjercicio.append(autocorreccionSlot);
  raiz.append(filaEjercicio);

  // Fila 3: `dashboardElemento` (enunciado + puntuación, ya un grid de dos
  // columnas propio) -- solo visible cuando hay un ejercicio seleccionado.
  if (dashboardElemento) {
    raiz.append(dashboardElemento);
  }

  // Fila 4: "Editor visual de diagramas" (`titulo`) + `layout` (function tabs a
  // todo el ancho, paleta + canvas, ejecución).
  raiz.append(titulo, layout);

  // ---- autoguardado local (TSK-0026, reutilizado tal cual) ----
  const autoguardado = crearAutoguardadoLocal(() => ({ programa, estadoVisual }), {
    usuarioId,
    ambito: ambitoBorrador,
  });

  function flushGuardadoInmediato() {
    guardarProgramaLocal(programa, estadoVisual, usuarioId, ambitoBorrador);
  }

  // Salvaguarda adicional (no sustituye el debounce anterior, lo complementa):
  // `canvas-view.js` no ofrece ningún hook de "posición cambiada" (solo
  // `onCambioPrograma`, que no se dispara al mover un bloque, ver regla de
  // arquitectura de ese módulo: mover NUNCA toca la IR). Sin poder modificar
  // ese módulo (fuera de rutas permitidas), un arrastre/movimiento por
  // flechas no dispara por sí solo un guardado. Se mitiga guardando de forma
  // síncrona e inmediata cuando la pestaña deja de estar visible o se va a
  // descargar, para no perder posiciones movidas justo antes de cerrar.
  function manejarVisibilidadOculta() {
    if (document.hidden) flushGuardadoInmediato();
  }
  window.addEventListener('beforeunload', flushGuardadoInmediato);
  document.addEventListener('visibilitychange', manejarVisibilidadOculta);

  // ---- widgets ----
  /** @type {ReturnType<typeof crearVistaCanvas>} */
  let canvasVista;
  /**
   * Anuncio de la live region del canvas en tránsito entre la vista que se
   * destruye y la que se crea (ver `reconstruirDependientes`/`crearCanvas`).
   * `null` fuera de ese instante.
   * @type {string | null}
   */
  let mensajeEstadoCanvasPendiente = null;
  /**
   * Borrado que la vista del canvas aún puede deshacer al instante, en
   * tránsito igual que el anuncio de arriba (DEC-180). `null` fuera de ese
   * instante.
   * @type {ReturnType<ReturnType<typeof crearVistaCanvas>['eliminacionPendiente']>}
   */
  let eliminacionCanvasPendiente = null;
  /**
   * Anuncio que sustituye al de la vista anterior en la próxima
   * reconstrucción (DEC-180): cuando el cambio se rechazó por el bloqueo de
   * edición, lo que decía la vista vieja («Bloque X eliminado…») es falso.
   * @type {string | null}
   */
  let mensajeEstadoCanvasForzado = null;
  /** @type {ReturnType<typeof crearPanelContenidoBloque>} */
  let contenidoVista;
  /** @type {ReturnType<typeof crearPanelEjecucion>} */
  let ejecucionVista;
  /** @type {ReturnType<typeof crearPanelAutocorreccion>} */
  let autocorreccionVista;

  /**
   * @param {NodoModelo} nuevoProgramaVista Programa devuelto por
   *   `canvas-view.js`/`block-content-panel.js`: el `programa` central
   *   actualizado si se está viendo "Principal", o el Programa envoltorio
   *   actualizado (ver `programaParaVista()`) si se está viendo el diagrama
   *   de una función.
   * @param {'canvas' | 'contenido'} origen
   */
  function manejarCambioPrograma(nuevoProgramaVista, origen) {
    // BLOQUEO DE EDICIÓN (2026-08-07). Deshabilitar la paleta no bastaba:
    // editar una expresión, borrar, conectar o deshacer no pasan por ella, y
    // el borrador del ejercicio sobrevive al intento cerrado -- así que se
    // podía fallar, arreglar el diagrama con calma y arrancar el cronómetro
    // justo antes de corregir. Es el escenario que este bloqueo existe para
    // cerrar, y seguía abierto (hallazgo de security-reviewer).
    //
    // Este es el punto ÚNICO por el que pasan todas las mutaciones de la IR,
    // vengan del canvas o del panel de contenido, así que rechazar aquí las
    // cierra todas a la vez. Mover un bloque NO pasa por aquí y sigue
    // permitido a propósito: cambiar de sitio algo no construye una solución.
    //
    // Tras rechazar se reconstruyen las vistas: el canvas ya había apuntado
    // el cambio en su copia interna, y sin esto enseñaría algo que el
    // controlador no tiene.
    if (edicionBloqueada()) {
      rechazarPorBloqueo();
      return;
    }

    // Snapshot de deshacer ANTES de mutar nada (ver comentario junto a
    // `historialDeshacer`): en este punto `programa`/`estadoVisual` todavía
    // son el estado PREVIO a este cambio, exactamente lo que hay que poder
    // recuperar.
    if (!(origen === 'contenido' && origenAnteriorParaHistorial === 'contenido')) {
      historialDeshacer.push({
        programaJson: serializarPrograma(programa),
        estadoVisualObj: estadoVisualAObjeto(estadoVisual),
      });
      if (historialDeshacer.length > LIMITE_HISTORIAL_DESHACER) historialDeshacer.shift();
    }
    origenAnteriorParaHistorial = origen;

    // TSK-0078 ("DECISIÓN 7"): reconciliación del Programa envoltorio. Si el
    // diagrama activo es una función, el cambio NUNCA se aplica al
    // `cuerpo` principal -- se reconcilia en `programa.funciones[i].cuerpo`
    // (inmutable), para que editar el diagrama de una función jamás toque
    // el programa principal ni otras funciones.
    const funcion = funcionActiva();
    if (funcion) {
      const nuevoCuerpo = /** @type {NodoModelo} */ (
        /** @type {Record<string, unknown>} */ (nuevoProgramaVista).cuerpo
      );
      programa = actualizarCuerpoFuncion(programa, funcion.id, nuevoCuerpo);
    } else {
      programa = nuevoProgramaVista;
    }
    origenUltimoCambio = origen;
    autoguardado.notificarCambio();
    // TSK-0088 ("DECISIÓN 10"): CUALQUIER origen reconstruye por el mismo
    // microtask inmediato de siempre, SIN debounce (ver la cabecera del
    // archivo, "DECISIÓN 10", para la reproducción real del bug de pérdida
    // de datos que descartó diferir esto para el origen `'contenido'`): lo
    // que evita destruir el `<input>` en el que el usuario está escribiendo
    // es que `reconstruirDependientes()` ya NO toca `contenidoVista` en
    // absoluto cuando el origen es `'contenido'` (ver esa función).
    programarReconstruccion();
  }

  /**
   * DEC-180: rechaza un cambio por el bloqueo de edición. Nada cambia en
   * `programa`; se rehacen las vistas desde él (el canvas o el panel ya
   * habían apuntado el cambio en su copia), como un cambio del lienzo para
   * que también se rehaga el panel de contenido, y el lienzo dice por qué en
   * vez de anunciar lo que no pasó.
   */
  function rechazarPorBloqueo() {
    mensajeEstadoCanvasForzado = MENSAJE_EDICION_BLOQUEADA;
    origenUltimoCambio = 'canvas';
    programarReconstruccion();
  }

  function programarReconstruccion() {
    if (reconstruccionPendiente) return;
    reconstruccionPendiente = true;
    globalThis.queueMicrotask(() => {
      reconstruccionPendiente = false;
      reconstruirDependientes();
    });
  }

  /**
   * Deshacer GENERAL (ver comentario junto a `historialDeshacer`): saca el
   * último snapshot de la pila y restaura `programa`/`estadoVisual` a como
   * estaban justo antes de esa mutación. No-op silencioso si no hay nada
   * que deshacer (pila vacía) -- el botón que lo invoca ya se deshabilita
   * en ese caso, pero esta función no depende de eso para ser segura.
   */
  function deshacerUltimaAccion() {
    // DEC-180: también pasa por el bloqueo. Sin esto, con el ejercicio sin
    // intento abierto, deshacía cambios del intento anterior.
    if (edicionBloqueada()) {
      rechazarPorBloqueo();
      return;
    }
    const snapshot = historialDeshacer.pop();
    if (!snapshot) return;
    origenAnteriorParaHistorial = null;

    programa = /** @type {NodoModelo} */ (deserializarPrograma(snapshot.programaJson));
    estadoVisual.posiciones.clear();
    for (const [id, posicion] of Object.entries(snapshot.estadoVisualObj)) {
      establecerPosicion(estadoVisual, id, posicion);
    }
    // Si el paso deshecho era "crear función" y la pestaña activa era
    // justo esa función recién creada, ya no existe en el `programa`
    // restaurado: se cae a "Principal" en vez de dejar la navegación
    // apuntando a un id fantasma.
    if (diagramaActivo !== 'principal' && !obtenerFunciones().some((f) => f.id === diagramaActivo)) {
      diagramaActivo = 'principal';
    }

    origenUltimoCambio = 'canvas';
    autoguardado.notificarCambio();
    programarReconstruccion();
  }

  /**
   * TSK-0088 ("DECISIÓN 10"): resuelve YA MISMO (de forma síncrona) una
   * reconstrucción que hubiera quedado pendiente en el microtask de
   * `programarReconstruccion()`, si la hay.
   *
   * Hallazgo real de test-reviewer (segunda ronda de revisión de esta
   * tarea, confirmado con un test reproducible en
   * `tests/accessibility/diagram-editor/editor-integration.spec.js`):
   * `programarReconstruccion()` difiere la reconstrucción a un
   * `queueMicrotask` -- necesario (ver ese comentario) para no destruir un
   * panel EN MITAD de su propio método. Pero si DOS acciones síncronas se
   * encadenan SIN que el motor de JS llegue a vaciar la cola de microtareas
   * entre medias (p.ej. el manejador del evento `input` de un campo del
   * panel de contenido, seguido INMEDIATAMENTE, en la misma pila de
   * llamadas, por un clic en la paleta que coloca un bloque nuevo -- ambos
   * dentro del mismo "tick" síncrono), la segunda acción puede llegar a
   * ejecutarse ANTES de que el microtask de la primera se haya procesado.
   * Para el único punto de este controlador donde eso importa de verdad
   * (`canvasVista.colocarBloque(...)`, más abajo -- `canvas-view.js`,
   * módulo "done" fuera de rutas permitidas, mantiene su PROPIA copia
   * interna de `programa` en su cierre, que solo se resincroniza cuando
   * este controlador lo destruye/reconstruye, ver la cabecera del archivo):
   * si `colocarBloque` actuara sobre una `canvasVista` desactualizada
   * (porque la reconstrucción del cambio de contenido anterior seguía
   * pendiente), el `programa` que devuelve PIERDE en silencio esa edición
   * pendiente -- la misma regresión de pérdida de datos que motivó toda
   * "DECISIÓN 10", reintroducida por una vía distinta (la ausencia de un
   * `await`/límite de tarea entre dos acciones, no un debounce).
   *
   * Se llama SIEMPRE justo antes de `canvasVista.colocarBloque(...)`
   * (`onSeleccionarBloque` de la paleta, más abajo): si no hay ninguna
   * reconstrucción pendiente, es un no-op (`reconstruccionPendiente` ya es
   * `false`); si la hay, la ejecuta ya mismo y desactiva la bandera para
   * que el `queueMicrotask` original, cuando le toque el turno, no
   * reconstruya una segunda vez.
   *
   * Límite conocido y documentado (no todos los orígenes `'canvas'` pasan
   * por este controlador): las acciones que `canvas-view.js` gestiona
   * ÍNTEGRAMENTE con sus propios listeners internos sobre su propio DOM
   * (arrastrar, Supr/Backspace, papelera, reordenar con flechas) NO pasan
   * por ninguna función de este archivo antes de leer su copia interna de
   * `programa` -- no hay ningún punto de intercepción posible sin reabrir
   * ese módulo (fuera de las rutas permitidas de esta tarea). En la
   * práctica esas acciones exigen que el usuario mueva el foco/el puntero
   * al PROPIO canvas primero (un gesto de usuario real y discreto, que el
   * navegador ya separa en tareas distintas con vaciado de microtareas
   * entre medias) -- el caso realmente alcanzable sin ninguna tarea
   * intermedia es justo el que cubre esta función: la paleta, gestionada
   * por este controlador.
   */
  function asegurarReconstruccionAlDia() {
    if (!reconstruccionPendiente) return;
    reconstruccionPendiente = false;
    reconstruirDependientes();
  }

  function crearCanvas() {
    // TSK-0088 ("DECISIÓN 10"): nodo DOM del canvas ANTERIOR (si lo hay),
    // capturado ANTES de reasignar `canvasVista` -- se retira explícitamente
    // más abajo. Nunca se usa `canvasSlot.replaceChildren(...)` para esto
    // (ver el comentario junto a `canvasSlot.append(contenidoSlot)`, más
    // arriba en este archivo, sobre por qué esa función retira y reinserta
    // TODOS los hijos, incluido `contenidoSlot`, perdiendo el foco de
    // cualquier `<input>` que tuviera dentro en ese instante).
    const elementoCanvasAnterior = canvasVista?.elemento ?? null;

    // Se consume UNA sola vez: lo fija `reconstruirDependientes()` justo antes
    // de destruir la vista anterior (ver allí el porqué) y se limpia aquí para
    // que un montaje posterior no reviva un anuncio viejo.
    const mensajeEstadoInicial = mensajeEstadoCanvasPendiente ?? undefined;
    mensajeEstadoCanvasPendiente = null;
    const eliminacionPendiente = eliminacionCanvasPendiente;
    eliminacionCanvasPendiente = null;

    // TSK-0078 ("DECISIÓN 7"): `programaParaVista()` -- el `programa`
    // central si se ve "Principal", o el Programa envoltorio de la función
    // activa -- nunca `programa` directamente.
    canvasVista = crearVistaCanvas({
      programa: programaParaVista(),
      estadoVisual,
      mensajeEstadoInicial,
      eliminacionPendiente,
      onCambioPrograma: (p) => manejarCambioPrograma(p, 'canvas'),
      onDeshacer: deshacerUltimaAccion,
      deshacerDisponible: historialDeshacer.length > 0,
      // TSK-0195 (DEC-069 punto 6/DEC-070): el caso de compilación CON
      // ÉXITO ya llega por `onCambioPrograma` de arriba (canvas-view.js
      // commitea `programaActual` y lo notifica ANTES de invocar este otro
      // callback, ver su cabecera "TSK-0195" junto a
      // `refrescarCapaInteractivaTrasEdicion`) -- `manejarCambioGrafoInteractivo`
      // solo actúa en el caso SIN éxito (grafo temporalmente inválido), para
      // no disparar una segunda reconstrucción redundante.
      onCambioGrafoInteractivo: manejarCambioGrafoInteractivo,
    });
    // El canvas es el único contenido de `canvasSlot` (el panel de contenido
    // ya no se ancla aquí: vive en `modalContenido`). Se reemplaza el nodo
    // anterior de forma dirigida (nunca `replaceChildren`), preservando el
    // resto de `canvasSlot` intacto.
    if (elementoCanvasAnterior && elementoCanvasAnterior.parentNode === canvasSlot) {
      canvasSlot.replaceChild(canvasVista.elemento, elementoCanvasAnterior);
    } else {
      canvasSlot.append(canvasVista.elemento);
    }
  }

  // `crearOperaciones()` (panel de operaciones, `block-operations.js`)
  // RETIRADA en TSK-0061: ver "DECISIÓN 4" en la cabecera. La papelera +
  // Supr/Backspace + "duplicar" mínimo ya viven dentro de `crearCanvas()`
  // (`canvas-view.js`), así que no hace falta ninguna función equivalente
  // aquí.

  // `crearConectores()` (panel de conectores, `connectors.js`) RETIRADA en
  // TSK-0062: ver "DECISIÓN 5" en la cabecera. Con la lista vertical, la
  // posición ES la conexión: no hace falta ninguna función equivalente
  // aquí, ni preservar el pool de "bloques sin conectar" entre
  // reconstrucciones (ese concepto deja de existir en este modelo).

  /**
   * Sincroniza el modal de contenido con la selección actual del panel
   * (`contenidoVista.obtenerIdSeleccionadoEnCanvas()`, API pública ya
   * existente de `block-content-panel.js` -- la selección NUNCA se
   * reimplementa aquí). Sustituye al antiguo anclaje del panel junto al
   * bloque (TSK-0064/TSK-0088), que se retira por completo:
   *   - Sin bloque seleccionado: cierra el modal si estuviera abierto.
   *   - Con bloque seleccionado: fija el título (etiqueta legible del tipo) y
   *     la descripción breve reutilizadas de la paleta, y abre el modal si no
   *     lo estaba (con el botón del bloque como `opener`, al que el modal
   *     devuelve el foco al cerrarse). Si ya estaba abierto (cambio de
   *     bloque), solo actualiza título/descripción: un único modal, sin
   *     duplicados.
   */
  /**
   * Fija título + descripción breve del modal a partir del TIPO del bloque
   * seleccionado (etiquetas reutilizadas de la paleta). Compartido por la
   * apertura explícita (doble clic / Enter) y por la actualización en vivo al
   * cambiar de bloque con el modal ya abierto.
   * @param {string} idSeleccionado
   */
  function aplicarTituloDescripcionContenido(idSeleccionado) {
    if (!canvasVista) return;
    const bloque = /** @type {HTMLElement | null} */ (
      canvasVista.elemento.querySelector(`[data-node-id="${idSeleccionado}"]`)
    );
    const tipo = bloque?.dataset.blockType ?? '';
    const definicion = definicionDeTipo(tipo);
    modalContenido.fijarTitulo(definicion?.etiqueta ?? 'Contenido del bloque');
    const descripcion = definicion?.descripcion;
    descripcionContenido.textContent = descripcion ?? '';
    descripcionContenido.hidden = !descripcion;
  }

  function sincronizarModalContenido() {
    if (!canvasVista || !contenidoVista) return;

    const idSeleccionado = contenidoVista.obtenerIdSeleccionadoEnCanvas();
    if (!idSeleccionado) {
      if (modalContenido.estaAbierto()) modalContenido.cerrar();
      return;
    }

    // Decisión de UX (petición del usuario): la SELECCIÓN de un bloque (un clic
    // o Tab) ya NO abre el modal, solo lo resalta. Abrir es ahora un gesto
    // EXPLÍCITO (doble clic con el ratón o Enter con el teclado), gestionado en
    // `abrirContenidoDeBloque` + los listeners delegados de `canvasSlot` más
    // abajo. Aquí, por tanto, solo se ACTUALIZA el modal cuando YA está abierto
    // (cambiar de bloque con el diálogo abierto): un único modal, sin duplicar
    // ni reabrir por focos programáticos.
    if (!modalContenido.estaAbierto()) return;
    aplicarTituloDescripcionContenido(idSeleccionado);
  }

  /**
   * Apertura EXPLÍCITA del modal de contenido para un bloque del canvas (doble
   * clic con el ratón o Enter con el teclado sobre el bloque). La mera
   * selección ya no abre (ver `sincronizarModalContenido`). Respeta
   * `suprimirAperturaModalContenido` para no reabrir durante el retorno de foco
   * al cerrar.
   * @param {HTMLElement} boton
   */
  function abrirContenidoDeBloque(boton) {
    if (!contenidoVista || suprimirAperturaModalContenido) return;
    const id = boton.dataset.nodeId;
    if (!id) return;
    // Asegura que el panel muestre ESTE bloque aunque el gesto no haya pasado
    // por la selección-por-clic previa (p. ej. un `dblclick` sintético).
    contenidoVista.seleccionarBloque(id);
    aplicarTituloDescripcionContenido(id);
    if (!modalContenido.estaAbierto()) modalContenido.abrir(boton);
  }

  // Gestos de APERTURA del modal de contenido, delegados en `canvasSlot` (el
  // contenedor ESTABLE del canvas, nunca destruido): doble clic con el ratón o
  // Enter con el teclado (paridad WCAG 2.1.1) sobre un bloque COLOCADO.
  // Restringido a `.canvas-block[data-node-id]`: las anclas Inicio/Fin
  // (`.canvas-anchor`) no tienen contenido editable. No se toca `canvas-view.js`
  // (zona protegida): `manejarTeclaEnBloque` ignora Enter y el bloque nunca tuvo
  // listener de `click`, así que estos gestos son puramente aditivos.
  canvasSlot.addEventListener('dblclick', (evento) => {
    const objetivo = /** @type {Element | null} */ (evento.target);
    const boton = objetivo?.closest?.('.canvas-block[data-node-id]');
    if (boton) abrirContenidoDeBloque(/** @type {HTMLElement} */ (boton));
  });
  canvasSlot.addEventListener('keydown', (evento) => {
    const teclado = /** @type {KeyboardEvent} */ (evento);
    if (teclado.key !== 'Enter') return;
    const objetivo = /** @type {Element | null} */ (teclado.target);
    const boton = objetivo?.closest?.('.canvas-block[data-node-id]');
    if (!boton) return;
    teclado.preventDefault();
    abrirContenidoDeBloque(/** @type {HTMLElement} */ (boton));
  });

  function crearContenido() {
    // Mismo contrato de DOM que "DECISIÓN 1" (canvas -> operaciones), ahora
    // también para el panel de contenido (TSK-0050/TSK-0051): consume la
    // selección del canvas por `canvasRoot` + `dataset.nodeId` + `focusin`,
    // nunca importando `canvas-view.js` directamente. Siempre recibe la raíz
    // del canvas RECIÉN reconstruido (nunca una raíz obsoleta), porque los 4
    // paneles se destruyen y recrean juntos en cada reconstrucción.
    // TSK-0078 ("DECISIÓN 7"): mismo `programaParaVista()` que `crearCanvas()`
    // -- deben ser exactamente el mismo Programa (mismos `id` de nodo) para
    // que la selección por `dataset.nodeId` del canvas resuelva contra el
    // árbol correcto en `buscarNodoPorId`/`reemplazarNodoPorId`.
    // TSK-0088 ("DECISIÓN 10"): `canvasRoot` es `canvasSlot` (el contenedor
    // ESTABLE, nunca destruido), NO `canvasVista.elemento` (que SÍ se
    // destruye/recrea en cada reconstrucción, incluidas las de origen
    // `'contenido'`, ver `reconstruirDependientes()`) -- `contenidoVista`
    // sobrevive a esas reconstrucciones de canvas sin destruirse, así que
    // sus propios listeners `focusin`/`mousedown`/`click` necesitan un
    // ancestro que siga en el documento pase lo que pase. `canvasSlot`
    // contiene en todo momento el `canvasVista.elemento` vigente (ver
    // `crearCanvas()`, que lo inserta/sustituye dirigidamente sin tocar
    // `contenidoSlot`), así que los eventos delegados de cualquier botón de
    // bloque siguen llegando sin importar cuántas veces se haya
    // reconstruido `canvasVista` por debajo.
    contenidoVista = crearPanelContenidoBloque({
      programa: programaParaVista(),
      canvasRoot: canvasSlot,
      onCambioPrograma: (p) => manejarCambioPrograma(p, 'contenido'),
      // Notifica CUALQUIER cambio de selección (abrir/cerrar/cambiar de
      // bloque, sea cual sea su origen: focusin, el toggle de clic, o una
      // llamada programática) -- abre/cierra/actualiza el modal de contenido.
      onSeleccionCambiada: () => sincronizarModalContenido(),
    });
    // El panel vive en el cuerpo del modal (con la descripción breve encima).
    modalContenido.cuerpo.replaceChildren(descripcionContenido, contenidoVista.elemento);
  }

  /**
   * Panel "Ejecutar diagrama" (TSK-0058, CAP-EDITOR-EXECUTION; originalmente
   * el sexto slot cuando operaciones y conectores aún se montaban, ambos
   * retirados desde entonces, ver "DECISIÓN 4"/"DECISIÓN 5"). A diferencia de
   * canvas/contenido, este panel nunca llama a `onCambioPrograma` (solo LEE
   * el `programa` central para ejecutarlo, no lo modifica), así que no
   * participa en ninguna decisión de origen de cambio ni necesita el
   * contrato de DOM de "DECISIÓN 1": recibe el `programa` ya actualizado
   * directamente, igual que `crearCanvas()`.
   *
   * TSK-0078 ("DECISIÓN 7"): recibe SIEMPRE el `programa` CENTRAL completo
   * (nunca `programaParaVista()`) -- "Ejecutar" ejecuta siempre el programa
   * principal, que ya incluye todas las funciones vía `Programa.funciones`.
   * Cuando el diagrama activo es una función, se antepone un aviso textual
   * explícito para que no quede ambiguo qué se va a ejecutar de verdad.
   *
   * Hallazgo de accessibility-reviewer (primera revisión de TSK-0078): este
   * aviso se inserta en el DOM vía `replaceChildren` como parte de
   * `reconstruirDependientes()` (cambiar de diagrama, o crear una función
   * nueva) -- sin `role="status"`/`aria-live`, un usuario de lector de
   * pantalla que no tuviera el foco exactamente ahí en ese instante nunca se
   * enteraría de que "Ejecutar" va a correr el programa completo, no el
   * cuerpo de la función que está viendo: exactamente el "cambio visual
   * silencioso" que el propio criterio de aceptación de la ficha pedía
   * evitar. Se corrige con el mismo patrón de live region ya usado en el
   * resto de este controlador/paneles (`autoguardadoEstado` más arriba,
   * `execution-panel.js#estado`): rol nativo "status" + `aria-live="polite"`
   * (moderada, no interrumpe -- no es un error, es contexto informativo) +
   * `aria-atomic="true"` (se anuncia el texto completo, no solo el delta).
   */
  /**
   * TSK-0195 (DEC-069 punto 6/DEC-070): acepta un `programaOverride`
   * opcional -- por defecto el `programa` central, exactamente el
   * comportamiento previo a esta tarea para cualquier llamador existente
   * (`reconstruirDependientes()`) -- que permite a
   * `manejarCambioGrafoInteractivo` forzar `null` cuando el grafo
   * interactivo está temporalmente inválido, sin tocar `programa` ni el
   * historial de deshacer/autoguardado.
   * @param {NodoModelo | null} [programaOverride]
   */
  function crearEjecucion(programaOverride = programa) {
    ejecucionVista = crearPanelEjecucion({ programa: programaOverride ?? undefined });
    const funcion = funcionActiva();
    if (funcion) {
      const aviso = document.createElement('p');
      aviso.className = 'diagram-editor-execution-scope-notice';
      aviso.setAttribute('role', 'status');
      aviso.setAttribute('aria-live', 'polite');
      aviso.setAttribute('aria-atomic', 'true');
      aviso.textContent = `Estás viendo el diagrama de la función "${funcion.nombre}". Al pulsar "Ejecutar" se ejecuta el programa Principal completo (incluye todas las funciones definidas), no solo este diagrama.`;
      ejecucionSlot.replaceChildren(aviso, ejecucionVista.elemento);
    } else {
      ejecucionSlot.replaceChildren(ejecucionVista.elemento);
    }
  }

  /**
   * Séptimo slot (TSK-0124, panel "Autocorregir ejercicio"). A diferencia de
   * `crearEjecucion()`, siempre recibe el `programa` CENTRAL completo
   * (nunca `programaParaVista()`): el backend evalúa el ejercicio contra el
   * programa Principal (misma decisión ya tomada para "Ejecutar", ver
   * "DECISIÓN 7" en la cabecera del archivo) -- no tiene sentido autocorregir
   * el cuerpo aislado de una función. `ejercicioIdActual`/`csrfTokenActual`/
   * `tiempoInicioMs` son closures del controlador (ver el docstring de
   * `crearControladorEditor`), reenviados tal cual en cada (re)construcción.
   */
  /**
   * TSK-0195 (DEC-069 punto 6/DEC-070): mismo `programaOverride` opcional
   * que `crearEjecucion` (ver su docstring), con idéntico motivo.
   * @param {NodoModelo | null} [programaOverride]
   */
  function crearAutocorreccion(programaOverride = programa) {
    autocorreccionVista = crearPanelAutocorreccion({
      programa: programaOverride ?? undefined,
      ejercicioId: ejercicioIdActual,
      csrfToken: csrfTokenActual,
      tiempoInicioMs,
      material: materialEjercicioActual,
      // Un ejercicio ya acertado no se repite (2026-08-07). El estado es
      // MUTABLE y vive aquí, no en el panel: el panel se destruye y se
      // recrea en cada `reconstruirDependientes()`, así que si lo guardara él
      // se perdería a la primera tecla que pulsara el alumno.
      ejercicioCompletado: ejercicioCompletadoActual,
      onCambioIntento: (hayIntentoAbierto) => {
        hayIntentoAbiertoActual = hayIntentoAbierto;
        aplicarBloqueoDeEdicion();
      },
      onEjercicioCompletado: () => {
        ejercicioCompletadoActual = true;
        if (typeof opciones.onEjercicioCompletado === 'function') {
          opciones.onEjercicioCompletado();
        }
      },
    });
    autocorreccionSlot.replaceChildren(autocorreccionVista.elemento);
  }

  /**
   * TSK-0195 (DEC-069 punto 6/DEC-070): reacciona a un recálculo del
   * resultado de compilación del grafo interactivo de `canvasVista`
   * (`onCambioGrafoInteractivo`, ver `crearCanvas()`). El caso "compila con
   * éxito" ya lo gestiona `onCambioPrograma` -> `manejarCambioPrograma` (el
   * propio `canvas-view.js` commitea `programaActual` y lo notifica ANTES
   * de invocar este callback, ver su cabecera junto a
   * `refrescarCapaInteractivaTrasEdicion`): no hay nada que hacer aquí en
   * ese caso, para no disparar una segunda reconstrucción redundante ni
   * interferir con el historial de deshacer/autoguardado ya actualizados
   * por ese camino.
   *
   * Si el grafo NO compila (temporalmente inválido, DEC-069 punto 3): NO se
   * toca `programa`/el historial de deshacer/el autoguardado -- se
   * conserva el último estado lógico válido -- pero SÍ se refrescan
   * ÚNICAMENTE "Ejecutar"/"Corrección" con `programa: null`, para que su
   * guarda ya existente (`esNodo(programa)`, `execution-panel.js`/
   * `autocorrection-panel.js`, fuera de las rutas permitidas de esta ficha)
   * bloquee la llamada al backend sin necesidad de tocar esos módulos
   * (DEC-070: la puerta es el ÉXITO de `compilarGrafoAIr`, nunca
   * `validarGrafoArcos`). La lista CONCRETA de errores ya es visible en el
   * propio canvas (`canvas-view.js#regionErroresCompilacion`), justo donde
   * se está editando el grafo -- `execution-panel.js`/
   * `autocorrection-panel.js` solo necesitan bloquearse, no repetir esa
   * lista.
   *
   * Cuidado real detectado por el propio test de integración de esta ficha
   * (`tests/accessibility/diagram-editor/editor-integration.spec.js`,
   * "TSK-0195"): `execution-panel.js#destruir()` (fuera de rutas permitidas)
   * NO retira `botonEjecutar` de `[data-canvas-toolbar-run]` -- solo lo hace
   * seguro destruir/recrear TODO `canvasVista` (que se lleva el contenedor
   * entero por delante), algo que este camino deliberadamente NO hace (ver
   * arriba: recrear `canvasVista` aquí perdería la edición de flecha
   * temporalmente inválida, silenciosamente, violando DEC-069 punto 3). Sin
   * este aviso, el botón "Ejecutar" ANTERIOR (ya sin listener, pero seguía
   * en el DOM) convivía con el nuevo, dejando DOS botones "Ejecutar" en la
   * barra de herramientas. Se retira aquí, explícitamente, ANTES de crear el
   * panel nuevo -- ninguna otra clase de `[data-canvas-toolbar-run]`
   * (la papelera, `.canvas-trash`) se ve afectada.
   * @param {import('./arrow-graph-compiler.js').ResultadoCompilacion} resultado
   */
  function manejarCambioGrafoInteractivo(resultado) {
    if (resultado.programa !== null) return;
    const contenedorBotonEjecutar = raiz.querySelector('[data-canvas-toolbar-run]');
    for (const botonHuerfano of contenedorBotonEjecutar?.querySelectorAll('.execution-panel-run') ?? []) {
      botonHuerfano.remove();
    }
    ejecucionVista?.destruir();
    autocorreccionVista?.destruir();
    crearEjecucion(null);
    crearAutocorreccion(null);
  }

  /**
   * Tipos de `<input>` en los que `HTMLInputElement#selectionStart`/
   * `setSelectionRange` son válidos según la especificación (WHATWG HTML,
   * "the input element": `text`, `search`, `url`, `tel`, `password` — más
   * el caso especial `undefined`/sin atributo `type`, que el propio HTML
   * trata como `text`). Cualquier otro tipo (`number`, `radio`, `checkbox`,
   * `range`, `color`, `date`...) lanza `InvalidStateError` en navegadores
   * reales al leer/escribir esa propiedad; JSDOM (el entorno de test) es
   * más permisivo y devuelve `null` en silencio, lo que ocultó este fallo
   * hasta la revisión de accessibility-reviewer de TSK-0051. `expression-
   * editor.js` (TSK-0049, "done", fuera de rutas permitidas) usa
   * precisamente `type="number"` para literales ENTERO/DECIMAL y
   * `type="radio"` para su selector de modo: ambos viven DENTRO de
   * `contenidoVista.elemento` cuando el campo editado es una expresión.
   * @type {ReadonlyArray<string>}
   */
  const TIPOS_INPUT_CON_SELECCION_DE_TEXTO = Object.freeze(['text', 'search', 'url', 'tel', 'password']);

  /**
   * Ver "DECISIÓN 3" en la cabecera. Se llama SIEMPRE antes de destruir
   * `contenidoVista`, pero solo devuelve datos si el foco realmente vivía en
   * uno de sus `<input>` (propios o de `expression-editor.js`, TSK-0049,
   * "done", fuera de rutas permitidas — cualquier `type`: `text`, `number`,
   * `radio`...). La SELECCIÓN de bloque y el `<input>` concreto a re-enfocar
   * se restauran SIEMPRE que el foco estuviera en un `<input>` de este
   * panel; la POSICIÓN DE CURSOR (`selectionStart`) solo se lee cuando el
   * `type` la soporta (`TIPOS_INPUT_CON_SELECCION_DE_TEXTO` arriba) —
   * leerla en un `<input type="number">`/`type="radio"` (los que usa
   * `expression-editor.js` para literales numéricos y el selector de modo)
   * lanza `InvalidStateError` en un navegador real (JSDOM, el entorno de
   * test, es más permisivo y devuelve `null` en silencio, lo que ocultó
   * este fallo hasta la revisión de accessibility-reviewer de TSK-0051).
   *
   * Nota: evita `instanceof HTMLInputElement` como identificador de código
   * (mismo motivo ya documentado en `palette-view.js#resolverBotonDeBloque`:
   * ese global de tipo DOM no está declarado en `eslint.config.js`, fuera de
   * las rutas permitidas de esta tarea, y `no-undef` lo rechazaría); usa en
   * su lugar `tagName === 'INPUT'` + `type` explícito.
   * @returns {{ idSeleccionado: string, indiceInput: number, posicionCursor: number | null } | null}
   */
  function capturarFocoContenido() {
    if (!contenidoVista) return null;
    const activo = /** @type {{ tagName?: string, type?: string, selectionStart?: number | null } | null} */ (
      document.activeElement
    );
    if (!activo || activo.tagName !== 'INPUT') return null;
    if (!contenidoVista.elemento.contains(/** @type {Node} */ (document.activeElement))) return null;
    const idSeleccionado = contenidoVista.obtenerIdSeleccionadoEnCanvas();
    if (!idSeleccionado) return null;
    const inputs = Array.from(contenidoVista.elemento.querySelectorAll('input'));
    const indiceInput = inputs.indexOf(/** @type {any} */ (document.activeElement));
    if (indiceInput === -1) return null;
    const puedeLeerCursor = TIPOS_INPUT_CON_SELECCION_DE_TEXTO.includes(activo.type ?? 'text');
    return { idSeleccionado, indiceInput, posicionCursor: puedeLeerCursor ? (activo.selectionStart ?? null) : null };
  }

  /**
   * Ver "DECISIÓN 3". Reselecciona el mismo bloque en la instancia NUEVA de
   * `contenidoVista` y devuelve el foco (con cursor) al `<input>`
   * equivalente. Devuelve `true` si logró restaurar el foco a un campo
   * concreto (en cuyo caso `reconstruirDependientes` NO debe aplicar además
   * la restauración de foco genérica).
   *
   * Defensa adicional (mismo motivo que el filtro de tipo en
   * `capturarFocoContenido`): `capturado` existe para CUALQUIER `<input>`
   * origen (la selección de bloque y el foco se restauran siempre), pero
   * `posicionCursor` solo es un número cuando el campo ORIGEN era de un
   * tipo con selección de texto válida. Aun así, se repite la misma
   * comprobación de `type` aquí sobre el `<input>` DESTINO (la instancia
   * nueva) antes de llamar a `setSelectionRange`, por si la estructura de
   * campos cambiase entre una reconstrucción y otra (p.ej. el usuario
   * cambia de modo "literal"/"variable" dentro de la misma pulsación que
   * dispara la reconstrucción) y el índice ya no apuntase a un campo del
   * mismo tipo que el de origen.
   * @param {{ idSeleccionado: string, indiceInput: number, posicionCursor: number | null } | null} capturado
   * @returns {boolean}
   */
  function restaurarFocoContenido(capturado) {
    if (!capturado) return false;
    contenidoVista.seleccionarBloque(capturado.idSeleccionado);
    const inputs = Array.from(contenidoVista.elemento.querySelectorAll('input'));
    const input = /** @type {{ focus: () => void, type?: string, setSelectionRange?: (a: number, b: number) => void } | undefined} */ (
      inputs[capturado.indiceInput]
    );
    if (!input) return false;
    input.focus();
    const posicionCursor = capturado.posicionCursor;
    const puedeRestaurarCursor =
      typeof posicionCursor === 'number' &&
      typeof input.setSelectionRange === 'function' &&
      TIPOS_INPUT_CON_SELECCION_DE_TEXTO.includes(input.type ?? 'text');
    if (puedeRestaurarCursor) {
      /** @type {(a: number, b: number) => void} */ (input.setSelectionRange)(posicionCursor, posicionCursor);
    }
    return true;
  }

  function reconstruirDependientes() {
    // TSK-0088 ("DECISIÓN 10", ver la cabecera del archivo para la
    // reproducción completa del bug que descarta diferir esto): cuando el
    // origen es `'contenido'`, `contenidoVista` NUNCA se destruye ni se
    // recrea -- ya refleja el cambio (su propio `programaActual` interno se
    // actualizó de forma síncrona al notificar `onCambioPrograma`) y el
    // usuario puede tener el foco/cursor en mitad de un `<input>` suyo. Solo
    // `canvasVista`/`ejecucionVista` se reconstruyen (necesario para que
    // `canvas-view.js` no se quede con una copia obsoleta de `programa` en
    // su cierre, ver la cabecera). Para cualquier OTRO origen
    // (`'canvas'`, o `null` en la reconstrucción inicial/tras crear-eliminar
    // una función) se reconstruyen los tres, comportamiento sin cambios.
    const esOrigenContenido = origenUltimoCambio === 'contenido';

    // Preserva la posición de scroll de la PÁGINA a través de la reconstrucción
    // (bug reportado: "al meter un bloque en las cajas Si se cumple / No se
    // cumple, la vista vuelve al inicio del canvas"). Destruir/recrear el DOM
    // del canvas, o el retorno de foco al bloque recién colocado, puede colapsar
    // momentáneamente la altura del documento y hacer que el navegador salte
    // arriba. Se captura ANTES de tocar el DOM y se restaura al terminar
    // (síncrono) y en el siguiente frame (por si el salto lo provoca un reflujo
    // asíncrono, p. ej. tras un arrastre HTML5). Solo restaura si algo lo movió,
    // para no interferir con un scroll intencionado del usuario.
    const scrollPaginaX = window.scrollX;
    const scrollPaginaY = window.scrollY;
    const restaurarScrollPagina = () => {
      if (window.scrollX !== scrollPaginaX || window.scrollY !== scrollPaginaY) {
        window.scrollTo(scrollPaginaX, scrollPaginaY);
      }
    };

    const focoContenidoPrevio = !esOrigenContenido ? capturarFocoContenido() : null;
    // Anuncio vigente de la live region del canvas, capturado ANTES de
    // destruirla. Reconstruir el canvas crea un `<p role="status">` nuevo, que
    // nacía con su texto por defecto y borraba lo que `canvas-view.js` acababa
    // de anunciar ("Bloque X colocado…", "Bloque X eliminado…", "Bloque X
    // movido a la posición…"): esos anuncios se emiten justo antes de
    // notificar el cambio de programa que dispara ESTA reconstrucción, así que
    // no sobrevivían ni una microtarea y un lector de pantalla no llegaba a
    // leer ninguno. Solo se propaga aquí (reconstrucción por cambio de
    // programa); un montaje inicial o un cambio de función siguen empezando
    // con el texto por defecto, que es lo correcto ahí.
    mensajeEstadoCanvasPendiente = mensajeEstadoCanvasForzado ?? canvasVista?.mensajeEstado() ?? null;
    mensajeEstadoCanvasForzado = null;
    // DEC-180: lo mismo para el «Deshacer eliminación de…» que la vista
    // acaba de enseñar, y su foco. Sin esto, el botón nuevo nacía oculto. Al
    // cambiar de diagrama (origen `null`) no se pasa: sería el borrado de
    // otro diagrama. La vista nueva, además, lo descarta si no cuadra con
    // el programa que dibuja.
    eliminacionCanvasPendiente = origenUltimoCambio === null ? null : (canvasVista?.eliminacionPendiente() ?? null);
    const deshacerEliminacionConFoco = canvasVista?.deshacerEliminacionTieneFoco() ?? false;
    // DEC-180: qué tenía el foco dentro del canvas, para dárselo al mismo
    // control de la vista nueva, y el bloque del borrado pendiente, por si la
    // vista nueva lo descarta porque el bloque volvió.
    const bloqueDelBorradoPrevio = canvasVista?.eliminacionPendiente()?.nodo.id ?? null;
    const focoCanvasPrevio = describirFocoDelCanvas();
    canvasVista?.destruir();
    if (!esOrigenContenido) {
      contenidoVista?.destruir();
    }
    ejecucionVista?.destruir();
    autocorreccionVista?.destruir();
    // Orden importa: contenido necesita `canvasSlot` ya creado (contiene el
    // `canvasVista.elemento` recién construido, contrato de DOM "DECISIÓN
    // 1"/"DECISIÓN 10"); ejecución/autocorrección son independientes (solo
    // leen `programa`, ver `crearEjecucion()`/`crearAutocorreccion()`).
    crearCanvas();
    if (!esOrigenContenido) {
      crearContenido();
    }
    crearEjecucion();
    crearAutocorreccion();
    // Tras cada reconstrucción, sincroniza el modal de contenido con la
    // selección vigente (título/descripción y apertura/cierre). En origen
    // `'contenido'` `contenidoVista` no se recreó (la selección no cambió),
    // así que esto es idempotente y no perturba el modal abierto.
    sincronizarModalContenido();
    if (esOrigenContenido) {
      // `contenidoVista` nunca se tocó: el foco (si estaba ahí) sigue
      // exactamente donde estaba, no hay nada que restaurar.
      restaurarScrollPagina();
      globalThis.requestAnimationFrame?.(restaurarScrollPagina);
      return;
    }
    if (deshacerEliminacionConFoco && canvasVista.enfocarDeshacerEliminacion()) {
      // El foco sigue en «Deshacer eliminación de…», como lo dejó la vista
      // anterior: un Intro basta para recuperar el bloque (TSK-0061).
    } else if (
      deshacerEliminacionConFoco &&
      enfocarEnCanvas(
        (el) => el.classList.contains('canvas-block') && el.dataset.nodeId === bloqueDelBorradoPrevio
      )
    ) {
      // DEC-180: el borrado se rechazó (edición bloqueada) y el bloque sigue
      // ahí: el foco, a él.
    } else if (!idParaEnfocar && devolverFocoAlCanvas(focoCanvasPrevio)) {
      // DEC-180: el mismo control que lo tenía (el bloque que se reordenó o se
      // recuperó, la tecla de la barra…). Antes caía al título del lienzo,
      // oculto a la vista, y la segunda ↓ ya no movía nada.
    } else if (!restaurarFocoContenido(focoContenidoPrevio)) {
      restaurarFoco();
    }
    restaurarScrollPagina();
    globalThis.requestAnimationFrame?.(restaurarScrollPagina);
  }

  /**
   * DEC-180: el control del canvas que tiene el foco, descrito para poder
   * reconocerlo en la vista nueva (la vieja se destruye): su primera clase,
   * los datos que lo identifican y su nombre. `null` si el foco no está en el
   * canvas.
   * @returns {{ clase: string, datos: Array<[string, string]>, nombre: string } | null}
   */
  function describirFocoDelCanvas() {
    const activo = document.activeElement;
    if (!canvasVista || !(activo instanceof HTMLElement) || !canvasVista.elemento.contains(activo)) return null;
    const clase = activo.classList[0];
    if (!clase) return null;
    /** @type {Array<[string, string]>} */
    const datos = [];
    for (const clave of DATOS_QUE_IDENTIFICAN) {
      const valor = activo.dataset[clave];
      if (valor !== undefined) datos.push([clave, valor]);
    }
    return { clase, datos, nombre: nombreDeControl(activo) };
  }

  /**
   * DEC-180: enfoca el primer control del canvas actual que cumpla
   * `criterio`, sin desplazar la página ni abrir el contenido del bloque.
   * `true` si lo consiguió: un control oculto o deshabilitado no recibe el
   * foco, y entonces se sigue con el siguiente destino.
   * @param {(el: HTMLElement) => boolean} criterio
   * @returns {boolean}
   */
  function enfocarEnCanvas(criterio) {
    if (!canvasVista) return false;
    const destino = Array.from(canvasVista.elemento.querySelectorAll('*')).find(
      (el) => el instanceof HTMLElement && criterio(el)
    );
    if (!(destino instanceof HTMLElement)) return false;
    suprimirAperturaModalContenido = true;
    destino.focus({ preventScroll: true });
    suprimirAperturaModalContenido = false;
    return document.activeElement === destino;
  }

  /**
   * @param {ReturnType<typeof describirFocoDelCanvas>} descripcion
   * @returns {boolean}
   */
  function devolverFocoAlCanvas(descripcion) {
    if (!descripcion) return false;
    const esElMismo = (/** @type {HTMLElement} */ el) =>
      el.classList.contains(descripcion.clase) &&
      descripcion.datos.every(([clave, valor]) => el.dataset[clave] === valor);
    // Si hay varios iguales (las dos teclas del zoom), el del mismo nombre.
    return (
      enfocarEnCanvas((el) => esElMismo(el) && nombreDeControl(el) === descripcion.nombre) ||
      enfocarEnCanvas(esElMismo)
    );
  }

  /** Restaura el foco tras una reconstrucción, con el mejor destino
   * disponible sin poder tocar los módulos internos (ver cabecera):
   * 1) el bloque recién colocado desde la paleta, si sigue existiendo
   *    (el único caso con un `data-node-id` estable y localizable); si no,
   * 2) el encabezado del panel que originó el cambio (siempre localizable,
   *    nunca dejamos el foco "perdido" en <body>). */
  function restaurarFoco() {
    if (idParaEnfocar) {
      const id = idParaEnfocar;
      idParaEnfocar = null;
      const candidatos = canvasVista.elemento.querySelectorAll('[data-node-id]');
      const boton = Array.from(candidatos).find(
        (el) => /** @type {HTMLElement} */ (el).dataset.nodeId === id
      );
      if (boton) {
        // Foco PROGRAMÁTICO tras colocar/reordenar un bloque: no debe abrir su
        // modal de contenido (ese `focusin` reabriría/abriría el modal y
        // rompería la manipulación por teclado del bloque). Se suprime la
        // apertura y se deja la selección del panel en `null`, coherente con
        // "modal cerrado"; una activación explícita (clic/Tab del usuario) sí
        // lo abrirá.
        suprimirAperturaModalContenido = true;
        // `preventScroll: true` (bug reportado: "al meter un bloque en las
        // cajas Si se cumple / No se cumple, la página hace scroll hacia
        // arriba"): restaurar el foco al bloque recién colocado NO debe arrastrar
        // la página para traerlo a la vista. El foco (para teclado) se mantiene.
        /** @type {HTMLElement} */ (boton).focus({ preventScroll: true });
        contenidoVista?.seleccionarBloque(null);
        suprimirAperturaModalContenido = false;
        return;
      }
    }

    const vistaOrigen = origenUltimoCambio === 'contenido' ? contenidoVista : canvasVista;
    const encabezado = encabezadoDe(vistaOrigen.elemento);
    if (encabezado) moverFocoA(encabezado);
  }

  // ------------------------------------------------------------------------
  // TSK-0078 ("DECISIÓN 7"): navegación entre "Principal" y el diagrama de
  // cada función + creación de funciones nuevas. Se monta una única vez
  // (como la paleta, más abajo): no depende de qué se esté viendo en el
  // canvas, solo de la lista de funciones y de `diagramaActivo`.
  //
  // Patrón de accesibilidad elegido, documentado explícitamente (no hay
  // ambigüedad real, pero se registra la decisión): botones nativos + un
  // contenedor `role="group"` con `aria-current="true"` sobre el botón
  // activo, NO el patrón ARIA "tablist"/"tab"/"tabpanel". Un `tablist` real
  // exigiría roving tabindex manual (solo la pestaña activa en el orden de
  // Tab, flechas para moverse entre las demás) y un `tabpanel` propio -- una
  // pieza de accesibilidad completa que no aporta nada aquí frente a
  // "varios botones nativos alcanzables por Tab, cada uno ya activable con
  // Enter/Espacio de forma nativa": exactamente el mismo criterio de
  // simplicidad ya aplicado en el resto de este controlador (papelera,
  // deshacer, duplicar -- todo `<button>` nativo, nunca un widget ARIA
  // compuesto sin necesidad real).
  // ------------------------------------------------------------------------

  /** Mismo criterio mínimo de identificador que `block-content-panel.js`
   * (constante privada allí, sin exportar -- ver "DECISIÓN 7" en la
   * cabecera del archivo): se duplica aquí como regex trivial, mismo
   * precedente ya documentado en ese módulo. */
  const PATRON_IDENTIFICADOR_FUNCION = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

  /**
   * @param {string} crudo
   * @returns {{ valido: true, valor: string } | { valido: false, mensaje: string }}
   */
  function validarNombreFuncionNueva(crudo) {
    const texto = crudo.trim();
    if (!PATRON_IDENTIFICADOR_FUNCION.test(texto)) {
      return {
        valido: false,
        mensaje: 'Escribe un identificador válido: empieza por una letra o "_", sin espacios ni símbolos.',
      };
    }
    // No lo pide explícitamente el criterio de aceptación ("nombre válido de
    // identificador"), pero dos funciones con el mismo `nombre` harían
    // ambigua cualquier `Llamada.nombre_funcion` que las invoque (el motor
    // resuelve por nombre): validación mínima derivada directamente de
    // "nombre válido", ver "DECISIÓN 7" en la cabecera del archivo.
    if (obtenerFunciones().some((funcion) => funcion.nombre === texto)) {
      return { valido: false, mensaje: `Ya existe una función llamada "${texto}". Elige otro nombre.` };
    }
    return { valido: true, valor: texto };
  }

  // ------------------------------------------------------------------------
  // TSK-0211 ("DECISIÓN 13" en la cabecera del archivo): parámetros de una
  // función (nombre + tipo primitivo), reutilizados tanto por el formulario
  // "Nueva función" (lista local) como por la sección "Parámetros de esta
  // función" (persiste de inmediato en `programa`).
  // ------------------------------------------------------------------------

  /** Mismo enum/patrón de selector ya usado en `expression-editor.js` para
   * el tipo de un literal (`TIPO_PRIMITIVO`/`ETIQUETA_TIPO_PRIMITIVO`, ese
   * módulo no exporta su propia constante de etiquetas): se duplica aquí
   * como constante trivial, mismo precedente ya documentado para
   * `PATRON_IDENTIFICADOR_FUNCION` arriba. */
  const ETIQUETA_TIPO_PRIMITIVO_PARAMETRO = Object.freeze({
    [TIPO_PRIMITIVO.ENTERO]: 'Entero',
    [TIPO_PRIMITIVO.DECIMAL]: 'Decimal',
    [TIPO_PRIMITIVO.CADENA]: 'Texto',
    [TIPO_PRIMITIVO.BOOLEANO]: 'Booleano',
  });

  /**
   * @param {string} idSelect
   * @param {string} tipoInicial
   * @returns {HTMLSelectElement}
   */
  function crearSelectorTipoParametro(idSelect, tipoInicial) {
    const select = document.createElement('select');
    select.id = idSelect;
    for (const tipo of Object.values(TIPO_PRIMITIVO)) {
      const opcion = document.createElement('option');
      opcion.value = tipo;
      opcion.textContent = ETIQUETA_TIPO_PRIMITIVO_PARAMETRO[tipo];
      select.append(opcion);
    }
    select.value = tipoInicial;
    return select;
  }

  // ------------------------------------------------------------------------
  // TSK-0217 ("DECISIÓN 14" en la cabecera del archivo): tipo de retorno de
  // una función (`Funcion.tipo_retorno`), sentinel opcional "Procedimiento
  // (no devuelve nada)" que aplica `null` -- mismo enum/etiquetas que el
  // selector de tipo de parámetro de arriba, mismo criterio de sentinel ya
  // usado por `Entrada.tipo` (TSK-0212, `block-content-panel.js`, no
  // importado aquí: rutas_prohibidas de esta tarea).
  // ------------------------------------------------------------------------

  /** Valor de `<option>` reservado para "procedimiento, no devuelve nada"
   * (nunca puede colisionar con un valor real de `TIPO_PRIMITIVO`, que son
   * todos minúsculas sin espacios ni guion bajo doble). */
  const VALOR_TIPO_RETORNO_PROCEDIMIENTO = '__procedimiento__';

  /**
   * Traduce `Funcion.tipo_retorno` (un valor de `TIPO_PRIMITIVO`, o
   * `null`/`undefined`/cualquier otra cosa no reconocida) al valor de
   * `<option>` correspondiente. Único punto de esta traducción -- lo
   * reutilizan tanto `crearSelectorTipoRetorno` (valor inicial) como
   * `actualizarSeccionParametrosFuncion` (resincronizar al cambiar de
   * pestaña), para no duplicar el criterio "¿es un `TIPO_PRIMITIVO`
   * reconocido?" en dos sitios.
   * @param {unknown} tipoRetorno
   * @returns {string}
   */
  function valorSelectDeTipoRetorno(tipoRetorno) {
    return typeof tipoRetorno === 'string' &&
      Object.values(TIPO_PRIMITIVO).includes(/** @type {any} */ (tipoRetorno))
      ? tipoRetorno
      : VALOR_TIPO_RETORNO_PROCEDIMIENTO;
  }

  /**
   * @param {string} idSelect
   * @param {unknown} tipoRetornoInicial `Funcion.tipo_retorno` vigente:
   *   un valor de `TIPO_PRIMITIVO`, o `null`/`undefined` (procedimiento).
   * @returns {HTMLSelectElement}
   */
  function crearSelectorTipoRetorno(idSelect, tipoRetornoInicial) {
    const select = document.createElement('select');
    select.id = idSelect;

    const opcionProcedimiento = document.createElement('option');
    opcionProcedimiento.value = VALOR_TIPO_RETORNO_PROCEDIMIENTO;
    opcionProcedimiento.textContent = 'Procedimiento (no devuelve nada)';
    select.append(opcionProcedimiento);

    for (const tipo of Object.values(TIPO_PRIMITIVO)) {
      const opcion = document.createElement('option');
      opcion.value = tipo;
      opcion.textContent = ETIQUETA_TIPO_PRIMITIVO_PARAMETRO[tipo];
      select.append(opcion);
    }

    select.value = valorSelectDeTipoRetorno(tipoRetornoInicial);
    return select;
  }

  /**
   * @param {string} crudo
   * @param {string[]} nombresExistentes Nombres de los DEMÁS parámetros de
   *   la misma función/lista (excluyendo el propio, ver cada llamador).
   * @returns {{ valido: true, valor: string } | { valido: false, mensaje: string }}
   */
  function validarNombreParametro(crudo, nombresExistentes) {
    const texto = crudo.trim();
    if (!PATRON_IDENTIFICADOR_FUNCION.test(texto)) {
      return {
        valido: false,
        mensaje: 'Escribe un nombre de parámetro válido: empieza por una letra o "_", sin espacios ni símbolos.',
      };
    }
    // Dos parámetros con el mismo nombre en la misma función harían
    // ambiguo el binding posicional del motor (`diagram_engine.py`):
    // validación mínima derivada de "nombre válido", mismo criterio ya
    // aplicado a nombres de función duplicados (`validarNombreFuncionNueva`).
    if (nombresExistentes.includes(texto)) {
      return { valido: false, mensaje: `Ya hay un parámetro llamado "${texto}" en esta función.` };
    }
    return { valido: true, valor: texto };
  }

  /**
   * @param {string[]} nombresExistentes
   * @returns {string}
   */
  function generarNombreParametroDisponible(nombresExistentes) {
    let indice = 1;
    let candidato = `parametro${indice}`;
    while (nombresExistentes.includes(candidato)) {
      indice += 1;
      candidato = `parametro${indice}`;
    }
    return candidato;
  }

  /**
   * Lista editable de parámetros (nombre + tipo primitivo): componente
   * GENÉRICO reutilizado tanto por el formulario "Nueva función" (lista
   * local, sin persistir hasta confirmar) como por la sección "Parámetros de
   * esta función" (persiste cada cambio de inmediato en `programa`, ver
   * "DECISIÓN 13" en la cabecera del archivo). Mismo patrón ya aprobado de
   * `block-content-panel.js#crearCampoListaExpresiones` (TSK-0209):
   * reconstruye su sub-DOM SOLO al añadir/quitar una fila (cambia el número
   * de controles); renombrar/cambiar tipo de una fila existente NUNCA
   * reconstruye la lista completa (conserva el foco/cursor del campo que se
   * está editando).
   * `etiquetaSufijo` (por defecto '', TSK-0211): los DOS puntos de edición
   * ("Nueva función"/"Parámetros de esta función") pueden estar visibles a
   * la vez (crear una función SEGUNDA mientras la pestaña de otra ya
   * existente está activa) -- sin un sufijo que los distinga, "Nombre del
   * parámetro 1"/"Añadir parámetro" de uno y otro serían nombres accesibles
   * IDÉNTICOS y ambiguos para un usuario de lector de pantalla (y para
   * `getByRole`, que exige un único resultado). El formulario "Nueva
   * función" pasa `' de la nueva función'`; la sección de la función activa
   * no pasa ninguno (nombre corto, es la única función cuyos parámetros se
   * editan de esa forma en un momento dado).
   * @param {{
   *   idBase: string,
   *   obtenerParametros: () => Array<{ nombre: string, tipo: string }>,
   *   escribirParametros: (parametros: Array<{ nombre: string, tipo: string }>) => void,
   *   crearParametroPorDefecto: (nombre: string) => { nombre: string, tipo: string },
   *   textoVacio: string,
   *   etiquetaAnadir: string,
   *   etiquetaSufijo?: string,
   * }} opciones
   * @returns {{ elemento: HTMLElement, renderizar: () => void, destruir: () => void }}
   */
  function crearListaEditableParametros({
    idBase,
    obtenerParametros,
    escribirParametros,
    crearParametroPorDefecto,
    textoVacio,
    etiquetaAnadir,
    etiquetaSufijo = '',
  }) {
    const contenedor = document.createElement('div');
    contenedor.className = 'diagram-editor-param-list';

    const lista = document.createElement('div');
    lista.className = 'diagram-editor-param-list-rows';

    const botonAnadir = document.createElement('button');
    botonAnadir.type = 'button';
    botonAnadir.className = 'diagram-editor-param-list-add';
    botonAnadir.textContent = etiquetaAnadir;

    contenedor.append(lista, botonAnadir);

    /** @type {Array<{ destruirFila: () => void, inputNombre: HTMLInputElement }>} */
    let filas = [];

    function renderizar() {
      for (const fila of filas) fila.destruirFila();
      filas = [];
      lista.replaceChildren();

      const parametros = obtenerParametros();
      if (parametros.length === 0) {
        const vacio = document.createElement('p');
        vacio.className = 'diagram-editor-param-list-empty';
        vacio.textContent = textoVacio;
        lista.append(vacio);
      }

      parametros.forEach((parametro, indice) => {
        const fila = document.createElement('div');
        fila.className = 'diagram-editor-param-row';

        const idNombre = `${idBase}-${indice}-nombre`;
        const idTipo = `${idBase}-${indice}-tipo`;
        const idError = `${idBase}-${indice}-error`;

        const etiquetaNombre = document.createElement('label');
        etiquetaNombre.setAttribute('for', idNombre);
        etiquetaNombre.textContent = `Nombre del parámetro ${indice + 1}${etiquetaSufijo}`;

        const inputNombre = document.createElement('input');
        inputNombre.type = 'text';
        inputNombre.id = idNombre;
        inputNombre.autocomplete = 'off';
        inputNombre.value = parametro.nombre;

        const errorNombre = document.createElement('p');
        errorNombre.className = 'diagram-editor-param-row-error';
        errorNombre.id = idError;
        errorNombre.setAttribute('role', 'alert');

        const manejarInputNombre = () => {
          const otros = obtenerParametros()
            .filter((_, i) => i !== indice)
            .map((p) => p.nombre);
          const validacion = validarNombreParametro(inputNombre.value, otros);
          if (!validacion.valido) {
            errorNombre.textContent = validacion.mensaje;
            inputNombre.setAttribute('aria-describedby', idError);
            inputNombre.setAttribute('aria-invalid', 'true');
            return;
          }
          errorNombre.textContent = '';
          inputNombre.removeAttribute('aria-describedby');
          inputNombre.removeAttribute('aria-invalid');
          const actuales = obtenerParametros();
          escribirParametros(actuales.map((p, i) => (i === indice ? { ...p, nombre: validacion.valor } : p)));
        };
        inputNombre.addEventListener('input', manejarInputNombre);

        const etiquetaTipo = document.createElement('label');
        etiquetaTipo.setAttribute('for', idTipo);
        etiquetaTipo.textContent = `Tipo del parámetro ${indice + 1}${etiquetaSufijo}`;

        const selectTipo = crearSelectorTipoParametro(idTipo, parametro.tipo);
        const manejarCambioTipo = () => {
          const actuales = obtenerParametros();
          escribirParametros(actuales.map((p, i) => (i === indice ? { ...p, tipo: selectTipo.value } : p)));
        };
        selectTipo.addEventListener('change', manejarCambioTipo);

        const botonQuitar = document.createElement('button');
        botonQuitar.type = 'button';
        botonQuitar.className = 'diagram-editor-param-row-remove';
        botonQuitar.textContent = `Quitar parámetro ${indice + 1}${etiquetaSufijo}`;
        const manejarQuitar = () => {
          const actuales = obtenerParametros();
          escribirParametros(actuales.slice(0, indice).concat(actuales.slice(indice + 1)));
          renderizar();
          // Foco gestionado (03-frontend.md §13.1): nunca cae a
          // document.body tras quitar una fila -- "Añadir parámetro" nunca
          // se destruye, mismo criterio que
          // `crearCampoListaExpresiones#quitarArgumento`.
          botonAnadir.focus();
        };
        botonQuitar.addEventListener('click', manejarQuitar);

        fila.append(etiquetaNombre, inputNombre, errorNombre, etiquetaTipo, selectTipo, botonQuitar);
        lista.append(fila);

        filas.push({
          destruirFila: () => {
            inputNombre.removeEventListener('input', manejarInputNombre);
            selectTipo.removeEventListener('change', manejarCambioTipo);
            botonQuitar.removeEventListener('click', manejarQuitar);
          },
          inputNombre,
        });
      });
    }

    function anadir() {
      const actuales = obtenerParametros();
      const nombreDefecto = generarNombreParametroDisponible(actuales.map((p) => p.nombre));
      escribirParametros(actuales.concat([crearParametroPorDefecto(nombreDefecto)]));
      renderizar();
      // Foco gestionado: al campo de NOMBRE de la fila recién creada (el
      // control que el alumno necesita rellenar de inmediato), seleccionando
      // su texto por defecto para que escribir lo sustituya sin más.
      const ultimaFila = filas[filas.length - 1];
      ultimaFila?.inputNombre.focus();
      ultimaFila?.inputNombre.select();
    }
    botonAnadir.addEventListener('click', anadir);

    renderizar();

    return {
      elemento: contenedor,
      renderizar,
      destruir: () => {
        for (const fila of filas) fila.destruirFila();
        botonAnadir.removeEventListener('click', anadir);
      },
    };
  }

  const idEncabezadoDiagramas = `diagram-editor-diagrams-heading-${idInstancia}`;
  const idErrorNuevaFuncion = `diagram-editor-new-function-error-${idInstancia}`;
  const idInputNuevaFuncion = `diagram-editor-new-function-input-${idInstancia}`;

  const seccionDiagramas = document.createElement('section');
  seccionDiagramas.className = 'diagram-editor-diagrams';
  seccionDiagramas.setAttribute('aria-labelledby', idEncabezadoDiagramas);

  const tituloDiagramas = document.createElement('h2');
  tituloDiagramas.id = idEncabezadoDiagramas;
  tituloDiagramas.className = 'diagram-editor-diagrams-heading';
  tituloDiagramas.textContent = 'Diagramas';

  const pestanasDiagramas = document.createElement('div');
  pestanasDiagramas.className = 'diagram-editor-diagram-tabs ui-segmentos';
  pestanasDiagramas.setAttribute('role', 'group');
  pestanasDiagramas.setAttribute('aria-label', 'Cambiar de diagrama');

  const botonNuevaFuncion = document.createElement('button');
  botonNuevaFuncion.type = 'button';
  botonNuevaFuncion.className = 'diagram-editor-new-function-toggle ui-button ui-button-terciario';
  // Rediseño (Fase 4b): «+» de trazo delante, decorativo.
  botonNuevaFuncion.append(crearIcono('mas'), document.createTextNode('Crear función nueva'));

  const formularioNuevaFuncion = document.createElement('form');
  formularioNuevaFuncion.className = 'diagram-editor-new-function-form';
  formularioNuevaFuncion.setAttribute('aria-label', 'Crear función nueva');

  const etiquetaNuevaFuncion = document.createElement('label');
  etiquetaNuevaFuncion.setAttribute('for', idInputNuevaFuncion);
  etiquetaNuevaFuncion.textContent = 'Nombre de la nueva función';

  const inputNuevaFuncion = document.createElement('input');
  inputNuevaFuncion.type = 'text';
  inputNuevaFuncion.id = idInputNuevaFuncion;
  inputNuevaFuncion.autocomplete = 'off';

  const errorNuevaFuncion = document.createElement('p');
  errorNuevaFuncion.className = 'diagram-editor-new-function-error';
  errorNuevaFuncion.id = idErrorNuevaFuncion;
  errorNuevaFuncion.setAttribute('role', 'alert');
  errorNuevaFuncion.hidden = true;

  // TSK-0211 ("DECISIÓN 13"): parámetros declarados en el momento de crear
  // la función -- lista LOCAL, nunca persistida en `programa` hasta
  // confirmar "Crear función" (ver `manejarSubmitNuevaFuncion`, más abajo).
  /** @type {Array<{ nombre: string, tipo: string }>} */
  let parametrosNuevaFuncion = [];

  const tituloParametrosNuevaFuncion = document.createElement('h3');
  tituloParametrosNuevaFuncion.className = 'diagram-editor-new-function-params-heading';
  tituloParametrosNuevaFuncion.textContent = 'Parámetros (opcional)';

  const listaParametrosNuevaFuncion = crearListaEditableParametros({
    idBase: `diagram-editor-new-function-param-${idInstancia}`,
    obtenerParametros: () => parametrosNuevaFuncion,
    escribirParametros: (nuevos) => {
      parametrosNuevaFuncion = nuevos;
    },
    crearParametroPorDefecto: (nombre) => ({ nombre, tipo: TIPO_PRIMITIVO.ENTERO }),
    textoVacio: 'Sin parámetros todavía.',
    etiquetaAnadir: 'Añadir parámetro a la nueva función',
    // TSK-0211: distingue estos controles de los de "Parámetros de esta
    // función" cuando ambos están visibles a la vez (crear una función
    // SEGUNDA mientras la pestaña de otra ya existente está activa).
    etiquetaSufijo: ' de la nueva función',
  });

  const accionesNuevaFuncion = document.createElement('div');
  accionesNuevaFuncion.className = 'diagram-editor-new-function-actions';

  const botonConfirmarNuevaFuncion = document.createElement('button');
  botonConfirmarNuevaFuncion.type = 'submit';
  botonConfirmarNuevaFuncion.textContent = 'Crear función';

  const botonCancelarNuevaFuncion = document.createElement('button');
  botonCancelarNuevaFuncion.type = 'button';
  botonCancelarNuevaFuncion.textContent = 'Cancelar';

  accionesNuevaFuncion.append(botonConfirmarNuevaFuncion, botonCancelarNuevaFuncion);
  formularioNuevaFuncion.append(
    etiquetaNuevaFuncion,
    inputNuevaFuncion,
    errorNuevaFuncion,
    tituloParametrosNuevaFuncion,
    listaParametrosNuevaFuncion.elemento,
    accionesNuevaFuncion
  );

  // "Crear función nueva" se muestra ahora en una ventana emergente (petición
  // del usuario). El `<form>` completo (nombre + parámetros + acciones) vive en
  // el cuerpo del modal; `botonNuevaFuncion` lo ABRE. Se usa `portal: true` (el
  // valor por defecto): al abrir, el overlay se traslada a `document.body`, de
  // modo que queda CENTRADO en la pantalla e inmune al `transform`/`overflow`
  // de los ancestros del editor (que antes, con `portal: false`, lo recortaban
  // y lo mostraban partido abajo a la izquierda -- corrección pedida por el
  // usuario, mismo patrón ya aplicado a los modales de pseudocódigo/python).
  const modalNuevaFuncion = crearModalDialogo({
    titulo: 'Crear función nueva',
    onCerrar: () => ocultarErrorNuevaFuncion(),
  });
  modalNuevaFuncion.elemento.classList.add('diagram-editor-new-function-modal');
  modalNuevaFuncion.cuerpo.append(formularioNuevaFuncion);

  // Confirmación de "Eliminar función" en ventana emergente (petición del
  // usuario). Sustituye la confirmación en DOS PASOS inline dentro de la propia
  // pestaña (TSK-0080). Un ÚNICO modal reutilizable: `pedirConfirmacionEliminar
  // Funcion` rellena el nombre de la función y lo abre; el botón "Eliminar"
  // ejecuta el MISMO borrado real de siempre (`eliminarFuncionDiagrama`), sin
  // tocar esa lógica. Al cerrarse (Escape, clic fuera, ✕ o "Cancelar") no borra
  // nada y limpia el id pendiente.
  const modalEliminarFuncion = crearModalDialogo({
    titulo: 'Eliminar función',
    onCerrar: () => {
      idPendienteConfirmarEliminarFuncion = null;
    },
  });
  modalEliminarFuncion.elemento.classList.add('diagram-editor-delete-function-modal');

  const parrafoEliminarFuncion = document.createElement('p');
  parrafoEliminarFuncion.className = 'diagram-editor-delete-function-text';
  modalEliminarFuncion.cuerpo.append(parrafoEliminarFuncion);

  const botonConfirmarEliminarFuncion = document.createElement('button');
  botonConfirmarEliminarFuncion.type = 'button';
  botonConfirmarEliminarFuncion.className = 'diagram-editor-diagram-tab-delete-confirm';
  botonConfirmarEliminarFuncion.textContent = 'Eliminar función';
  botonConfirmarEliminarFuncion.addEventListener('click', () => {
    const id = idPendienteConfirmarEliminarFuncion;
    if (!id) return;
    // Cerrar ANTES de borrar: `cerrar()` devuelve el foco a la pestaña que
    // abrió el modal (todavía existe en este instante); acto seguido
    // `eliminarFuncionDiagrama` elimina esa pestaña y mueve el foco a
    // "Principal" (estado final estable), como ya hacía antes.
    modalEliminarFuncion.cerrar();
    eliminarFuncionDiagrama(id);
  });

  const botonCancelarEliminarFuncion = document.createElement('button');
  botonCancelarEliminarFuncion.type = 'button';
  botonCancelarEliminarFuncion.className = 'diagram-editor-diagram-tab-delete-cancel';
  botonCancelarEliminarFuncion.textContent = 'Cancelar';
  botonCancelarEliminarFuncion.addEventListener('click', () => cancelarEliminarFuncion());

  modalEliminarFuncion.acciones.append(botonCancelarEliminarFuncion, botonConfirmarEliminarFuncion);

  seccionDiagramas.append(
    tituloDiagramas,
    pestanasDiagramas,
    botonNuevaFuncion,
    modalNuevaFuncion.elemento,
    modalEliminarFuncion.elemento
  );
  diagramasSlot.append(seccionDiagramas);

  // TSK-0211 ("DECISIÓN 13"): sección "Parámetros de esta función", visible
  // SOLO mientras la pestaña activa es una función (nunca en "Principal",
  // que no tiene parámetros propios). A diferencia de `seccionDiagramas`
  // (creación/navegación entre diagramas), esta sección persiste cada
  // cambio de inmediato en `programa.funciones[i].parametros` -- ver
  // `actualizarParametrosFuncion`/`actualizarSeccionParametrosFuncion`, aquí
  // debajo.
  const idEncabezadoParametrosFuncion = `diagram-editor-function-params-heading-${idInstancia}`;

  const seccionParametrosFuncion = document.createElement('section');
  seccionParametrosFuncion.className = 'diagram-editor-function-params';
  seccionParametrosFuncion.setAttribute('aria-labelledby', idEncabezadoParametrosFuncion);
  seccionParametrosFuncion.hidden = true;

  const tituloParametrosFuncion = document.createElement('h2');
  tituloParametrosFuncion.id = idEncabezadoParametrosFuncion;
  tituloParametrosFuncion.className = 'diagram-editor-function-params-heading';

  /**
   * Reemplaza de forma inmutable `programa.funciones[i].parametros` para la
   * función `funcionId` (TSK-0211: `editor-model.js` no expone ninguna
   * función de reemplazo genérica de una `Funcion` por `id` -- se replica
   * aquí el MISMO patrón inmutable ya usado por `actualizarCuerpoFuncion`,
   * mismo módulo, sin reabrirlo -- rutas_prohibidas de esta tarea).
   * @param {string} funcionId
   * @param {NodoModelo[]} nuevosParametros
   */
  function actualizarParametrosFuncion(funcionId, nuevosParametros) {
    // DEC-180: el bloqueo de edición (los controles ya salen deshabilitados;
    // esto es por si algo los llama igualmente).
    if (edicionBloqueada()) {
      actualizarSeccionParametrosFuncion();
      return;
    }
    programa = {
      ...programa,
      funciones: obtenerFunciones().map((f) => (f.id === funcionId ? { ...f, parametros: nuevosParametros } : f)),
    };
    autoguardado.notificarCambio();
  }

  const listaParametrosFuncion = crearListaEditableParametros({
    idBase: `diagram-editor-function-param-${idInstancia}`,
    // `Funcion.parametros`/`crearParametro` son `NodoModelo` reales (llevan
    // `id`/`nodo` además de `nombre`/`tipo`, ver editor-model.js): el
    // componente compartido solo necesita leer/escribir `nombre`/`tipo`, así
    // que se cruza el tipo aquí (vía `unknown`, mismo patrón de cast ya
    // usado en el resto de este archivo) sin perder esos dos campos extra
    // al reemplazar el array completo en `escribirParametros`.
    obtenerParametros: () =>
      /** @type {Array<{ nombre: string, tipo: string }>} */ (
        /** @type {unknown} */ (funcionActiva()?.parametros ?? [])
      ),
    escribirParametros: (nuevos) => {
      const funcion = funcionActiva();
      if (!funcion) return;
      actualizarParametrosFuncion(
        /** @type {string} */ (funcion.id),
        /** @type {NodoModelo[]} */ (/** @type {unknown} */ (nuevos))
      );
    },
    crearParametroPorDefecto: (nombre) =>
      /** @type {{ nombre: string, tipo: string }} */ (
        /** @type {unknown} */ (crearParametro({ nombre, tipo: TIPO_PRIMITIVO.ENTERO }))
      ),
    textoVacio: 'Esta función no recibe parámetros todavía.',
    etiquetaAnadir: 'Añadir parámetro',
  });

  // TSK-0217 ("DECISIÓN 14" en la cabecera del archivo): control de
  // `Funcion.tipo_retorno`, en la misma sección que los parámetros (ver esa
  // decisión para el porqué de no duplicarlo también en "Crear función
  // nueva"). `idSelectTipoRetorno` sigue el mismo esquema de ids que el
  // resto de controles por instancia (`idInstancia`, más abajo/arriba).
  const idSelectTipoRetorno = `diagram-editor-function-return-type-${idInstancia}`;

  const contenedorTipoRetorno = document.createElement('div');
  contenedorTipoRetorno.className = 'diagram-editor-return-type';

  const etiquetaTipoRetorno = document.createElement('label');
  etiquetaTipoRetorno.setAttribute('for', idSelectTipoRetorno);
  etiquetaTipoRetorno.textContent = 'Tipo de valor que devuelve esta función';

  const selectTipoRetorno = crearSelectorTipoRetorno(idSelectTipoRetorno, funcionActiva()?.tipo_retorno ?? null);

  /**
   * Persiste `Funcion.tipo_retorno` para la función activa (TSK-0217,
   * DEC-076): a diferencia de `actualizarParametrosFuncion` (arriba, que
   * replica el patrón inmutable localmente porque `editor-model.js` estaba
   * fuera de las rutas_permitidas de TSK-0211), esta tarea SÍ puede tocar
   * ese módulo, así que delega directamente en
   * `actualizarTipoRetornoFuncion` (editor-model.js) en vez de duplicar el
   * `.map(...)` inmutable aquí también.
   */
  function manejarCambioTipoRetorno() {
    const funcion = funcionActiva();
    if (!funcion) return;
    // DEC-180: el bloqueo de edición, igual que los parámetros.
    if (edicionBloqueada()) {
      actualizarSeccionParametrosFuncion();
      return;
    }
    const nuevoTipoRetorno =
      selectTipoRetorno.value === VALOR_TIPO_RETORNO_PROCEDIMIENTO ? null : selectTipoRetorno.value;
    programa = actualizarTipoRetornoFuncion(programa, /** @type {string} */ (funcion.id), nuevoTipoRetorno);
    autoguardado.notificarCambio();
  }
  selectTipoRetorno.addEventListener('change', manejarCambioTipoRetorno);

  contenedorTipoRetorno.append(etiquetaTipoRetorno, selectTipoRetorno);

  seccionParametrosFuncion.append(tituloParametrosFuncion, listaParametrosFuncion.elemento, contenedorTipoRetorno);
  diagramasSlot.append(seccionParametrosFuncion);

  /**
   * Sincroniza visibilidad/encabezado/filas de "Parámetros de esta función"
   * (incluido el selector de tipo de retorno, TSK-0217) con la pestaña
   * actualmente activa (`funcionActiva()`). Se llama tras cualquier cambio
   * que pueda alterar CUÁL es la función activa (cambiar de pestaña, crear
   * una función nueva, eliminar la activa) -- nunca dispara por sí misma
   * ninguna reconstrucción de canvas/contenido/ejecución (ni los parámetros
   * ni el tipo de retorno forman parte del `cuerpo` de la función, ver
   * "DECISIÓN 7").
   */
  function actualizarSeccionParametrosFuncion() {
    const funcion = funcionActiva();
    if (!funcion) {
      seccionParametrosFuncion.hidden = true;
      return;
    }
    seccionParametrosFuncion.hidden = false;
    tituloParametrosFuncion.textContent = `Parámetros de la función "${funcion.nombre}"`;
    listaParametrosFuncion.renderizar();
    selectTipoRetorno.value = valorSelectDeTipoRetorno(funcion.tipo_retorno);
    aplicarBloqueoAParametros();
  }

  /**
   * DEC-180: los parámetros y el tipo de retorno cambian `programa` sin pasar
   * por `manejarCambioPrograma`. Con la edición bloqueada, sus controles
   * salen deshabilitados, como la paleta.
   */
  function aplicarBloqueoAParametros() {
    const bloqueada = edicionBloqueada();
    for (const control of seccionParametrosFuncion.querySelectorAll('input, select, button')) {
      /** @type {HTMLInputElement} */ (control).disabled = bloqueada;
    }
  }

  /** @type {Map<string, HTMLButtonElement>} */
  const botonesPorDiagrama = new Map();

  /**
   * @param {string} id
   * @param {string} etiqueta
   * @returns {HTMLButtonElement}
   */
  function crearBotonDiagrama(id, etiqueta) {
    const boton = document.createElement('button');
    boton.type = 'button';
    boton.className = 'diagram-editor-diagram-tab ui-segmento';
    boton.textContent = etiqueta;
    boton.addEventListener('click', () => cambiarDiagramaActivo(id));
    botonesPorDiagrama.set(id, boton);
    return boton;
  }

  /**
   * TSK-0080 ("DECISIÓN 8"): control de borrado de la pestaña de una
   * función, nunca de "Principal" (criterio de aceptación explícito: "no es
   * borrable"). Envuelve el botón de la propia pestaña (`crearBotonDiagrama`)
   * junto con, según `idPendienteConfirmarEliminarFuncion`, o bien el botón
   * "Eliminar" que inicia la confirmación, o bien el par
   * "Confirmar eliminar"/"Cancelar" -- mismo patrón de dos pasos que
   * `block-operations.js#crearFila` (TSK-0024).
   * @param {{ id: string, nombre: string }} funcion
   * @returns {HTMLElement}
   */
  function crearPestanaFuncion(funcion) {
    const { id, nombre } = funcion;
    const contenedor = document.createElement('div');
    contenedor.className = 'diagram-editor-diagram-tab-wrapper';
    contenedor.append(crearBotonDiagrama(id, nombre));

    const eliminar = document.createElement('button');
    eliminar.type = 'button';
    eliminar.className = 'diagram-editor-diagram-tab-delete';
    eliminar.dataset.functionId = id;
    // Mockup aprobado (imagen de referencia, 2026-07-20): "×" compacto junto
    // al nombre de la pestaña, estilo píldora. El nombre accesible completo
    // ("Eliminar función X", criterio de aceptación original de TSK-0080:
    // nunca un icono decorativo sin nombre accesible) se conserva intacto
    // vía `aria-label` -- un lector de pantalla sigue anunciando el mismo
    // texto autodescriptivo de siempre, solo cambia lo que ve un usuario
    // vidente.
    eliminar.setAttribute('aria-label', `Eliminar función "${nombre}"`);
    eliminar.textContent = '×';
    eliminar.addEventListener('click', () => pedirConfirmacionEliminarFuncion(id));
    contenedor.append(eliminar);
    return contenedor;
  }

  /**
   * Reconstruye la lista COMPLETA de pestañas: se llama cuando cambia el
   * NÚMERO de diagramas (crear/eliminar una función), o cuando cambia el
   * paso de confirmación de borrado de una pestaña (TSK-0080), nunca al
   * solo cambiar cuál está activo -- `actualizarEstadoActivoNav()` (más
   * abajo) hace eso último sin recrear ningún botón, preservando el foco si
   * vivía en uno de ellos (mismo motivo que "DECISIÓN 3": no destruir el
   * control que el usuario acaba de activar).
   */
  function renderizarPestanasDiagramas() {
    pestanasDiagramas.replaceChildren();
    botonesPorDiagrama.clear();
    pestanasDiagramas.append(crearBotonDiagrama('principal', 'Principal'));
    for (const funcion of obtenerFunciones()) {
      pestanasDiagramas.append(
        crearPestanaFuncion({
          id: /** @type {string} */ (funcion.id),
          nombre: /** @type {string} */ (funcion.nombre),
        })
      );
    }
    actualizarEstadoActivoNav();
  }

  /**
   * TSK-0080 ("DECISIÓN 8") + revisión (modal, petición del usuario): inicia
   * la confirmación para la pestaña `id` abriendo el modal "Eliminar función"
   * con el nombre de la función. El propio modal gestiona el foco (lo atrapa y
   * lo devuelve al botón "×" que lo abrió al cerrarse); ya no se re-renderizan
   * las pestañas para mostrar controles inline.
   * @param {string} id
   */
  function pedirConfirmacionEliminarFuncion(id) {
    idPendienteConfirmarEliminarFuncion = id;
    const funcion = obtenerFunciones().find((f) => f.id === id);
    const nombre = /** @type {string} */ (funcion?.nombre ?? '');
    // DEC-180: con la edición bloqueada, el diálogo dice por qué no se puede
    // y no ofrece confirmar.
    const bloqueada = edicionBloqueada();
    parrafoEliminarFuncion.textContent = bloqueada
      ? MENSAJE_EDICION_BLOQUEADA
      : `¿Eliminar la función "${nombre}"? Esta acción no se puede deshacer.`;
    botonConfirmarEliminarFuncion.hidden = bloqueada;
    // `abrir()` sin `opener` explícito toma `document.activeElement` (el botón
    // "×" recién pulsado), al que devolverá el foco si se cancela/cierra.
    modalEliminarFuncion.abrir();
  }

  /**
   * TSK-0080 ("DECISIÓN 8") + revisión (modal): cancela sin borrar nada. Cierra
   * el modal (que devuelve el foco al botón "×" que lo abrió) y limpia el id
   * pendiente. `onCerrar` también limpia el id ante Escape/clic fuera/✕.
   */
  function cancelarEliminarFuncion() {
    idPendienteConfirmarEliminarFuncion = null;
    modalEliminarFuncion.cerrar();
  }

  /**
   * TSK-0080 ("DECISIÓN 8"): borra de verdad la función `id` de `programa`
   * (`eliminarFuncion`, editor-model.js). Si era la pestaña actualmente
   * activa, vuelve a "Principal" ANTES de reconstruir canvas/contenido/
   * ejecución, para que ninguno de los tres intente pedir el diagrama de
   * una función que ya no existe (criterio de aceptación explícito de la
   * ficha: "sin dejar el canvas en un estado roto"). Si NO era la activa,
   * el diagrama visible no se toca en absoluto (otro criterio de
   * aceptación explícito): solo se reconstruye la lista de pestañas.
   * @param {string} id
   */
  function eliminarFuncionDiagrama(id) {
    if (edicionBloqueada()) return;
    programa = eliminarFuncion(programa, id);
    idPendienteConfirmarEliminarFuncion = null;
    const eraActiva = diagramaActivo === id;
    if (eraActiva) {
      diagramaActivo = 'principal';
      origenUltimoCambio = null;
      idParaEnfocar = null;
    }
    autoguardado.notificarCambio();
    renderizarPestanasDiagramas();
    // "Principal" siempre existe (pestaña fija, no borrable): destino de
    // foco estable tanto si la función borrada era la activa (el usuario
    // acaba de "volver" ahí visualmente) como si no (su propio botón
    // "Eliminar..." ya no existe, no hay ningún control de esa pestaña al
    // que devolver el foco).
    botonesPorDiagrama.get('principal')?.focus();
    if (eraActiva) {
      // TSK-0211: "Parámetros de esta función" mostraba los de la función
      // recién borrada -- se oculta (vuelve a "Principal", que no tiene
      // parámetros propios) antes de reconstruir el resto.
      actualizarSeccionParametrosFuncion();
      reconstruirDependientes();
    }
  }

  /** Marca visual/semánticamente cuál pestaña está activa (`aria-current`),
   * SIN recrear ningún botón. */
  function actualizarEstadoActivoNav() {
    for (const [id, boton] of botonesPorDiagrama) {
      if (id === diagramaActivo) {
        boton.setAttribute('aria-current', 'true');
      } else {
        boton.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Cambia el diagrama mostrado en el canvas. Cambiar de diagrama NO
   * modifica ningún dato del `programa` (ver "DECISIÓN 7"): no se notifica
   * al autoguardado ni se marca ningún origen de cambio de contenido, solo
   * se reconstruyen canvas/contenido/ejecución con la vista correspondiente
   * -- exactamente el mecanismo que garantiza el criterio de aceptación
   * "volver a Principal muestra el programa principal exactamente como
   * estaba, sin pérdida de cambios" (el `programa` central nunca se toca al
   * navegar, solo se lee de forma distinta).
   * @param {'principal' | string} nuevoDiagramaActivo
   */
  function cambiarDiagramaActivo(nuevoDiagramaActivo) {
    if (nuevoDiagramaActivo === diagramaActivo) return;
    diagramaActivo = nuevoDiagramaActivo;
    origenUltimoCambio = null;
    idParaEnfocar = null;
    actualizarEstadoActivoNav();
    // TSK-0211: "Parámetros de esta función" depende de cuál es la pestaña
    // activa -- se sincroniza en cada cambio de diagrama, igual que
    // `actualizarEstadoActivoNav()` de la línea anterior.
    actualizarSeccionParametrosFuncion();
    reconstruirDependientes();
  }

  /** @param {string} mensaje */
  function mostrarErrorNuevaFuncion(mensaje) {
    errorNuevaFuncion.hidden = false;
    errorNuevaFuncion.textContent = mensaje;
    inputNuevaFuncion.setAttribute('aria-describedby', idErrorNuevaFuncion);
    inputNuevaFuncion.setAttribute('aria-invalid', 'true');
  }

  function ocultarErrorNuevaFuncion() {
    errorNuevaFuncion.hidden = true;
    errorNuevaFuncion.textContent = '';
    inputNuevaFuncion.removeAttribute('aria-describedby');
    inputNuevaFuncion.removeAttribute('aria-invalid');
  }

  function mostrarFormularioNuevaFuncion() {
    inputNuevaFuncion.value = '';
    ocultarErrorNuevaFuncion();
    // TSK-0211: la lista de parámetros local se reinicia en cada apertura
    // del formulario (nunca arrastra parámetros de un intento anterior
    // cancelado).
    parametrosNuevaFuncion = [];
    listaParametrosNuevaFuncion.renderizar();
    // Abre la ventana emergente y coloca el foco en el nombre (el modal atrapa
    // el foco y lo devuelve al botón al cerrarse).
    modalNuevaFuncion.abrir(botonNuevaFuncion);
    inputNuevaFuncion.focus();
  }

  function ocultarFormularioNuevaFuncion() {
    modalNuevaFuncion.cerrar();
    ocultarErrorNuevaFuncion();
  }

  function manejarCancelarNuevaFuncion() {
    ocultarFormularioNuevaFuncion();
  }

  botonNuevaFuncion.addEventListener('click', mostrarFormularioNuevaFuncion);
  botonCancelarNuevaFuncion.addEventListener('click', manejarCancelarNuevaFuncion);

  /** @param {SubmitEvent} evento */
  function manejarSubmitNuevaFuncion(evento) {
    evento.preventDefault();
    // DEC-180: el bloqueo de edición también aquí; el formulario lo explica.
    if (edicionBloqueada()) {
      mostrarErrorNuevaFuncion(MENSAJE_EDICION_BLOQUEADA);
      return;
    }
    const validacion = validarNombreFuncionNueva(inputNuevaFuncion.value);
    if (!validacion.valido) {
      mostrarErrorNuevaFuncion(validacion.mensaje);
      return;
    }
    // TSK-0211 ("DECISIÓN 13"): los parámetros declarados en el formulario
    // (lista local, ver `parametrosNuevaFuncion`) se convierten aquí en
    // nodos `Parametro` reales (`crearParametro`, editor-model.js) y se
    // pasan a `crearFuncion` -- antes de esta tarea `parametros` quedaba
    // siempre `[]`. Cada entrada ya llegó validada como identificador y sin
    // colisión de nombre (ver `crearListaEditableParametros`), así que
    // `crearParametro` nunca lanza aquí.
    const parametros = parametrosNuevaFuncion.map((p) => crearParametro({ nombre: p.nombre, tipo: p.tipo }));
    const funcion = crearFuncion({ nombre: validacion.valor, parametros });
    programa = agregarFuncion(programa, funcion);
    diagramaActivo = /** @type {string} */ (funcion.id);
    origenUltimoCambio = null;
    idParaEnfocar = null;
    autoguardado.notificarCambio();
    ocultarFormularioNuevaFuncion();
    parametrosNuevaFuncion = [];
    listaParametrosNuevaFuncion.renderizar();
    renderizarPestanasDiagramas();
    actualizarSeccionParametrosFuncion();
    reconstruirDependientes();
  }
  formularioNuevaFuncion.addEventListener('submit', manejarSubmitNuevaFuncion);

  renderizarPestanasDiagramas();
  // TSK-0211: estado inicial de "Parámetros de esta función" (oculta,
  // `diagramaActivo` empieza en `'principal'`, ver la declaración de esa
  // variable más arriba).
  actualizarSeccionParametrosFuncion();

  // ---- paleta: no depende de "programa", se monta una única vez ----
  /**
   * ¿Se puede tocar el diagrama ahora mismo?
   *
   * Regla (decisión del usuario, 2026-08-07): trabajando un EJERCICIO hace
   * falta un intento abierto; SIN ejercicio, libre. Ese segundo caso es el
   * modo borrador -- donde aterrizan las traducciones y donde se practica --
   * y bloquearlo dejaría el editor sin servir para trastear.
   *
   * El motivo de bloquear no es formal: sin esto, el alumno podía construir
   * la solución entera con calma y pulsar "Iniciar intento" un segundo antes
   * de corregir. El cronómetro medía diez segundos de un trabajo de media
   * hora, así que ni el tiempo del intento ni cualquier señal basada en él
   * significaban nada.
   * @returns {boolean}
   */
  function edicionBloqueada() {
    return ejercicioIdActual !== null && !hayIntentoAbiertoActual;
  }

  /** Refleja `edicionBloqueada()` en la paleta. */
  function aplicarBloqueoDeEdicion() {
    const bloqueada = edicionBloqueada();
    paletaVista?.establecerHabilitada(!bloqueada, bloqueada ? MENSAJE_EDICION_BLOQUEADA : undefined);
    aplicarBloqueoAParametros();
  }

  const paletaVista = crearVistaPaleta({
    onSeleccionarBloque: (nodo, definicion) => {
      if (nodo && typeof nodo.id === 'string') {
        idParaEnfocar = nodo.id;
      }
      // TSK-0088 ("DECISIÓN 10"): asegura que `canvasVista` refleje el
      // `programa` más reciente ANTES de que actúe sobre su copia interna --
      // ver `asegurarReconstruccionAlDia()` para la explicación completa del
      // hallazgo real de test-reviewer que motiva esta llamada.
      asegurarReconstruccionAlDia();
      // `colocarBloque` da su propio feedback explícito (aviso anunciado)
      // cuando `nodo` es `null` (Inicio/Fin/Conector): se reutiliza tal
      // cual, sin reimplementar ese caso aquí (docs/03-frontend.md, "no
      // duplicar lógica ya existente").
      canvasVista.colocarBloque(nodo, definicion);
    },
  });
  paletaSlot.append(paletaVista.elemento);

  crearCanvas();
  crearContenido();
  crearEjecucion();
  crearAutocorreccion();
  sincronizarModalContenido();

  function enfocarInicial() {
    moverFocoA(titulo);
  }

  function destruir() {
    topbar?.destruir();
    window.removeEventListener('beforeunload', flushGuardadoInmediato);
    document.removeEventListener('visibilitychange', manejarVisibilidadOculta);
    // Listener único registrado sobre `canvasSlot` (nunca destruido junto con
    // `canvasVista`), ver el punto donde se creó.
    canvasSlot.removeEventListener('focusin', manejarFocusInCanvasSlot);
    // TSK-0078: listeners propios de la navegación entre diagramas
    // (mismo patrón de limpieza explícita que el resto de listeners de
    // este método; los botones de `pestanasDiagramas` se recrean en cada
    // `renderizarPestanasDiagramas()` y se retiran del documento junto con
    // el resto de la composición, sin necesitar limpieza individual).
    botonNuevaFuncion.removeEventListener('click', mostrarFormularioNuevaFuncion);
    botonCancelarNuevaFuncion.removeEventListener('click', manejarCancelarNuevaFuncion);
    formularioNuevaFuncion.removeEventListener('submit', manejarSubmitNuevaFuncion);
    modalNuevaFuncion.destruir();
    // Retira el overlay del modal de confirmación de borrado de `document.body`
    // (donde lo deja el portal al abrirse); sin esto quedaría huérfano.
    modalEliminarFuncion.destruir();
    // TSK-0211: listeners propios de las dos listas editables de parámetros
    // (formulario "Nueva función" + sección "Parámetros de esta función").
    listaParametrosNuevaFuncion.destruir();
    listaParametrosFuncion.destruir();
    // TSK-0217: listener propio del selector de tipo de retorno.
    selectTipoRetorno.removeEventListener('change', manejarCambioTipoRetorno);
    autoguardado.detener();
    flushGuardadoInmediato();
    paletaVista.destruir();
    canvasVista.destruir();
    contenidoVista.destruir();
    // Retira el overlay del modal de contenido de `document.body` (donde lo
    // deja el portal al abrirse); sin esto quedaría huérfano.
    modalContenido.destruir();
    ejecucionVista.destruir();
    autocorreccionVista.destruir();
  }

  /**
   * TSK-0124: setter llamado por `diagram-editor-page.js` cuando la carga
   * asíncrona del ejercicio (`obtenerEjercicioPorSlug`, TSK-0054) resuelve
   * y ya se conoce su `id` (UUID) real -- ver el docstring de
   * `crearControladorEditor` y la cabecera de `autocorrection-panel.js`.
   * Reconstruye ÚNICAMENTE el panel de autocorrección (no todo el editor,
   * a diferencia de `reconstruirDependientes()`): esta actualización no
   * afecta a `programa`/canvas/contenido/ejecución, así que destruir/
   * recrear esos paneles sería trabajo innecesario y, peor, podría robarle
   * el foco a quien ya esté editando el diagrama mientras la petición de
   * red del enunciado sigue en vuelo.
   * @param {string | null} ejercicioId
   * @param {import('./autocorrection-panel.js').MaterialEjercicio} [material]
   *   Material pedagógico público del ejercicio, OPCIONAL: sin él el panel
   *   sigue funcionando igual, solo con menos peldaños de ayuda.
   */
  function establecerEjercicioActual(ejercicioId, material) {
    ejercicioIdActual = typeof ejercicioId === 'string' && ejercicioId.trim() !== '' ? ejercicioId : null;
    materialEjercicioActual = material ?? {};
    autocorreccionVista?.destruir();
    crearAutocorreccion();
    // Pasar a trabajar un ejercicio BLOQUEA el lienzo hasta iniciar intento;
    // dejar de trabajarlo lo libera. Va después de recrear el panel, que es
    // quien informa de si hay intento abierto.
    aplicarBloqueoDeEdicion();
  }

  /**
   * Marca si el alumno YA acertó el ejercicio que se está trabajando.
   *
   * Existe aparte de `establecerEjercicioActual` porque el dato llega por
   * otro camino y más tarde: el enunciado (`EjercicioPublico`) no dice si
   * está superado; eso solo lo sabe la LISTA de ejercicios del bloque, que la
   * página pide en paralelo. Reconstruye solo el panel de corrección, igual
   * que su hermana y por el mismo motivo.
   * @param {boolean} completado
   */
  function establecerEjercicioCompletado(completado) {
    const nuevo = completado === true;
    if (nuevo === ejercicioCompletadoActual) return;
    ejercicioCompletadoActual = nuevo;
    autocorreccionVista?.destruir();
    crearAutocorreccion();
  }

  // Estado inicial del bloqueo. VA AQUÍ, al final de la construcción, y no
  // antes: `paletaVista` es un `const` declarado más arriba y llamarlo antes
  // rompía con "Cannot access 'paletaVista' before initialization" -- la zona
  // muerta temporal, que el `?.` no evita.
  aplicarBloqueoDeEdicion();

  return {
    elemento: raiz,
    enfocarInicial,
    establecerEjercicioCompletado,
    destruir,
    obtenerPrograma: () => programa,
    obtenerEstadoVisual: () => estadoVisual,
    establecerEjercicioActual,
  };
}
